<template>
  <div class="landing pro text-body bg-body">
    <!-- Nav -->
    <SiteNav />

    <!-- Hero -->
    <section id="hero" class="position-relative overflow-hidden">
      <div class="hero-aurora hero-aurora--one" aria-hidden="true"></div>
      <div class="hero-aurora hero-aurora--two" aria-hidden="true"></div>
      <div class="hero-grid" aria-hidden="true"></div>

      <div class="container hero-container position-relative">
        <div class="row align-items-center g-5">
          <div class="col-lg-6">
            <div class="hero-kicker reveal">
              <span class="hero-kicker__mark" aria-hidden="true"></span>
              {{ $t('hero.kicker') }}
            </div>
            <h1 class="hero-title reveal d1">
              {{ $t('hero.title_lead') }}
              <span>{{ $t('hero.title_highlight') }}</span>
            </h1>
            <p class="hero-subtitle reveal d2">{{ $t('hero.subtitle') }}</p>

            <div class="hero-actions reveal d3">
              <a class="btn hero-primary btn-lg btn-press" href="#contactForm">
                {{ $t('hero.request_live_demo') }}
                <span aria-hidden="true">→</span>
              </a>
              <a class="hero-secondary" href="#showcase">
                <span class="hero-secondary__play" aria-hidden="true">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3.25 2.2v7.6L9.5 6 3.25 2.2Z" fill="currentColor"/></svg>
                </span>
                {{ $t('hero.explore_product') }}
              </a>
            </div>
            <p class="hero-demo-note reveal d3">{{ $t('hero.demo_note') }}</p>

            <div class="hero-scale reveal d3" :aria-label="$t('hero.scale.eyebrow')">
              <p class="hero-scale__eyebrow">{{ $t('hero.scale.eyebrow') }}</p>
              <div class="hero-scale__metrics">
                <div class="hero-scale__item"><strong>14K+</strong><span>{{ $t('hero.scale.users.label') }}</span></div>
                <div class="hero-scale__divider" aria-hidden="true"></div>
                <div class="hero-scale__item"><strong>25K+</strong><span>{{ $t('hero.scale.bookings.label') }}</span></div>
                <div class="hero-scale__divider" aria-hidden="true"></div>
                <div class="hero-scale__item"><strong>32K+</strong><span>{{ $t('hero.scale.sales.label') }}</span></div>
              </div>
            </div>

            <div class="hero-proof-list reveal d3" :aria-label="$t('hero.proof_label')">
              <div class="hero-proof">
                <span class="hero-proof__icon" aria-hidden="true">
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="6.5" y="2.5" width="11" height="19" rx="3" stroke="currentColor" stroke-width="1.8"/><path d="M10 18.5h4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
                </span>
                <span><strong>{{ $t('hero.proof.app.title') }}</strong>{{ $t('hero.proof.app.text') }}</span>
              </div>
              <div class="hero-proof">
                <span class="hero-proof__icon" aria-hidden="true">
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 12.5 9 17l11-11" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </span>
                <span><strong>{{ $t('hero.proof.support.title') }}</strong>{{ $t('hero.proof.support.text') }}</span>
              </div>
              <div class="hero-proof">
                <span class="hero-proof__icon" aria-hidden="true">
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8"/><path d="M3.5 12h17M12 3c2.3 2.45 3.5 5.45 3.5 9S14.3 18.55 12 21c-2.3-2.45-3.5-5.45-3.5-9S9.7 5.45 12 3Z" stroke="currentColor" stroke-width="1.8"/></svg>
                </span>
                <span><strong>{{ $t('hero.proof.language.title') }}</strong>{{ $t('hero.proof.language.text') }}</span>
              </div>
            </div>
          </div>

          <div class="col-lg-6">
            <div class="hero-product hero-ecosystem reveal d2" aria-hidden="true">
              <div class="hero-product__glow" aria-hidden="true"></div>

              <div class="hero-connection hero-connection--one"></div>
              <div class="hero-connection hero-connection--two"></div>

              <div class="hero-ops-panel">
                <div class="hero-ops-panel__head">
                  <div class="hero-ops-panel__identity">
                    <span class="hero-ops-panel__logo">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 18V8.5A3.5 3.5 0 0 1 9.5 5h5A3.5 3.5 0 0 1 18 8.5V18" stroke="currentColor" stroke-width="1.7"/><path d="M3.5 18h17M8.5 18v-4h7v4M9 9h.01M12 9h.01M15 9h.01" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg>
                    </span>
                    <span><small>{{ $t('hero.visual.command_center') }}</small><strong>{{ $t('hero.visual.today_studio') }}</strong></span>
                  </div>
                  <span class="hero-ops-panel__live">
                    <span class="hero-live-dot"></span>{{ $t('hero.visual.live') }}
                  </span>
                </div>

                <div class="hero-ops-stats">
                  <div class="hero-ops-stat">
                    <span>{{ $t('hero.visual.bookings_today') }}</span>
                    <strong>38</strong>
                    <small>+9 {{ $t('hero.visual.today') }}</small>
                  </div>
                  <div class="hero-ops-stat">
                    <span>{{ $t('hero.visual.revenue') }}</span>
                    <strong>€4.5K</strong>
                    <small>+12%</small>
                  </div>
                  <div class="hero-ops-stat">
                    <span>{{ $t('hero.visual.fill_rate') }}</span>
                    <strong>86%</strong>
                    <small>+7%</small>
                  </div>
                </div>

                <div class="hero-ops-schedule">
                  <div class="hero-ops-schedule__title">
                    <strong>{{ $t('hero.visual.next_classes') }}</strong>
                    <span>{{ $t('hero.visual.view_schedule') }} →</span>
                  </div>
                  <div class="hero-class-row">
                    <time>17:30</time>
                    <span><strong>Reformer Flow</strong><small>{{ $t('hero.visual.with') }} Elena</small></span>
                    <span class="hero-class-row__capacity is-full">12/12</span>
                  </div>
                  <div class="hero-class-row">
                    <time>18:30</time>
                    <span><strong>Strength Lab</strong><small>{{ $t('hero.visual.with') }} Andreas</small></span>
                    <span class="hero-class-row__capacity">15/18</span>
                  </div>
                  <div class="hero-class-row">
                    <time>20:00</time>
                    <span><strong>Slow Flow Yoga</strong><small>{{ $t('hero.visual.with') }} Sofia</small></span>
                    <span class="hero-class-row__capacity">8/12</span>
                  </div>
                </div>
              </div>

              <div class="hero-member-phone">
                <div class="hero-member-phone__notch"></div>
                <div class="hero-member-phone__top">
                  <span class="hero-member-phone__brand"><img src="/logo.png" alt="" width="26" height="26"><strong>{{ $t('hero.visual.your_studio') }}</strong></span>
                  <span class="hero-member-phone__avatar">M</span>
                </div>
                <p>{{ $t('hero.visual.welcome_member') }}</p>
                <div class="hero-member-glance">
                  <div><small>{{ $t('hero.visual.credits') }}</small><strong>8</strong></div>
                  <div><small>{{ $t('hero.visual.active_bookings') }}</small><strong>2</strong></div>
                </div>
                <div class="hero-member-upcoming">
                  <span>{{ $t('hero.visual.up_next') }}</span>
                  <div class="hero-member-upcoming__title"><strong>Reformer Flow</strong><span>✓</span></div>
                  <small>{{ $t('hero.visual.today') }} · 17:30 · {{ $t('hero.visual.spot') }} R4</small>
                </div>
                <div class="hero-member-phone__nav">
                  <span class="is-active"><svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="m4 10 8-6 8 6v9H4v-9Z" stroke="currentColor" stroke-width="1.8"/><path d="M9 19v-5h6v5" stroke="currentColor" stroke-width="1.8"/></svg></span>
                  <span><svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="4" width="18" height="17" rx="3" stroke="currentColor" stroke-width="1.8"/><path d="M7 2v4M17 2v4M3 9h18" stroke="currentColor" stroke-width="1.8"/></svg></span>
                  <span><svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 8h12l-1 12H7L6 8Z" stroke="currentColor" stroke-width="1.8"/><path d="M9 9V6a3 3 0 0 1 6 0v3" stroke="currentColor" stroke-width="1.8"/></svg></span>
                  <span><svg width="15" height="15" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="8" r="4" stroke="currentColor" stroke-width="1.8"/><path d="M4.5 21a7.5 7.5 0 0 1 15 0" stroke="currentColor" stroke-width="1.8"/></svg></span>
                </div>
              </div>

              <div class="hero-event-card hero-event-card--payment">
                <span class="hero-event-card__icon is-blue">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="2.5" y="5" width="19" height="14" rx="3" stroke="currentColor" stroke-width="1.8"/><path d="M2.5 9.5h19M6 15h4" stroke="currentColor" stroke-width="1.8"/></svg>
                </span>
                <span><small>{{ $t('hero.visual.payment_received') }}</small><strong>€89.00</strong></span>
              </div>

              <div class="hero-event-card hero-event-card--waitlist">
                <span class="hero-event-card__icon is-green">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 12.5 9 17l11-11" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </span>
                <span><small>{{ $t('hero.visual.automation') }}</small><strong>{{ $t('hero.visual.waitlist_filled') }}</strong></span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Logos / Partners -->
    <section class="container py-4 pb-0">
      <h2 class="h2 fw-bold text-center reveal">{{ $t('logos.tagline') }}</h2>

      <!-- Infinite marquee scroller -->
      <div class="logo-scroller reveal" role="region" aria-label="Partner logos" :class="{ 'logo-scroller--animate': animateLogos }">
        <div class="logo-track">
          <div v-for="(logo, i) in logos" :key="'lga-' + i" class="logo-item">
            <component :is="logo.url ? 'a' : 'div'"
                       class="logo-link"
                       :href="logo.url || null"
                       target="_blank"
                       rel="noopener"
            >

              <img :src="logo.img" :alt="logo.name" :title="logo.name" width="160" height="80">

              <div class="logo-caption" :class="logo.classes">{{ logo.name }}</div>
            </component>
          </div>
          <div v-for="(logo, i) in animatedLogoDuplicates" :key="'lgb-' + i" class="logo-item" aria-hidden="true">
            <div class="logo-link">
              <img :src="logo.img" alt="" :title="logo.name" width="160" height="80">
              <div class="logo-caption" :class="logo.classes">{{ logo.name }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- What do you want to do? (goal hub) -->
    <section id="product-areas" class="container py-5" aria-labelledby="hub-heading">
      <div class="text-center pb-4">
        <p class="solution-link__eyebrow mx-auto">{{ $t('hub.eyebrow') }}</p>
        <h2 id="hub-heading" class="h2 fw-bold reveal">{{ $t('hub.heading') }}</h2>
        <p class="text-secondary reveal mb-0">{{ $t('hub.subheading') }}</p>
      </div>
      <div class="row g-3">
        <div v-for="goal in [...productAreas, ...capabilities]" :key="goal.href" class="col-md-6 col-lg-4">
          <a :href="seoUrl(goal.href)" @click="navTo($event, seoUrl(goal.href))" class="card card-pro solution-link h-100 text-decoration-none text-dark reveal">
            <div class="card-body p-4">
              <span class="product-area-icon" aria-hidden="true" v-html="goal.icon"></span>
              <h3 class="h5 fw-semibold mt-3 mb-2">{{ $t(goal.title) }}</h3>
              <p class="text-secondary small mb-0">{{ $t(goal.text) }}</p>
              <div class="solution-link__cta">
                <span>{{ $t('hub.cta') }}</span>
                <span aria-hidden="true" class="solution-link__arrow">→</span>
              </div>
            </div>
          </a>
        </div>
      </div>
    </section>


    <!-- Bookings Features (Glassy) -->
    <section id="bookings" class="container py-5 pt-0">
      <div class="">
        <div class="row align-items-center g-4" style="margin: calc(var(--bs-gutter-x) * 0.5)">
          <div class="col-lg-6 bookings-glass card-pro reveal">
            <h2 class="h3 fw-bold mb-2">{{ $t('bookings.heading') }}</h2>
            <p class="text-secondary mb-4">{{ $t('bookings.subheading') }}</p>

            <ul class="list-unstyled m-0 p-0 bookings-list">
              <li class="d-flex align-items-start gap-3 mb-3">
                <span class="icon-badge-sm" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="3" y="4" width="18" height="16" rx="2" stroke="#10b981" stroke-width="1.6"/><path d="M8 2v4M16 2v4M3 10h18" stroke="#10b981" stroke-width="1.6"/></svg>
                </span>
                <div>
                  <div class="fw-semibold">{{ $t('bookings.items.cancellationNoticePeriod.title') }}</div>
                  <div class="text-secondary small">{{ $t('bookings.items.cancellationNoticePeriod.text') }}</div>
                </div>
              </li>
              <li class="d-flex align-items-start gap-3 mb-3">
                <span class="icon-badge-sm" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 6v6l4 2" stroke="#6366f1" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/><circle cx="12" cy="12" r="9" stroke="#6366f1" stroke-width="1.6"/></svg>
                </span>
                <div>
                  <div class="fw-semibold">{{ $t('bookings.items.enableLateCancellation.title') }}</div>
                  <div class="text-secondary small">{{ $t('bookings.items.enableLateCancellation.text') }}</div>
                </div>
              </li>
              <li class="d-flex align-items-start gap-3 mb-3">
                <span class="icon-badge-sm" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M5 12l5 5L20 7" stroke="#06b6d4" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </span>
                <div>
                  <div class="fw-semibold">{{ $t('bookings.items.allowWaitingList.title') }}</div>
                  <div class="text-secondary small">{{ $t('bookings.items.allowWaitingList.text') }}</div>
                </div>
              </li>
              <li class="d-flex align-items-start gap-3">
                <span class="icon-badge-sm" aria-hidden="true">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="4" y="5" width="16" height="14" rx="3" stroke="#f59e42" stroke-width="1.6"/><path d="M8 12h8" stroke="#f59e42" stroke-width="1.6" stroke-linecap="round"/></svg>
                </span>
                <div>
                  <div class="fw-semibold">{{ $t('bookings.items.suspendWaitlistBeforeClass.title') }}</div>
                  <div class="text-secondary small">{{ $t('bookings.items.suspendWaitlistBeforeClass.text') }}</div>
                </div>
              </li>
            </ul>
            <a :href="seoUrl('/class-booking-software/')" @click="navTo($event, seoUrl('/class-booking-software/'))" class="btn btn-link px-0 mt-3 fw-semibold text-decoration-none">
              {{ $t('bookings.learnMore') }} <span aria-hidden="true">→</span>
            </a>
          </div>

          <div class="col-lg-6 p-0">
            <div class="bookings-visual reveal slide-in-right d1" @mouseenter="stopBookingAutoplay(true)" @mouseleave="startBookingAutoplay()">
              <div class="shot">
<!--                <span :style="{'backgroundImage': `url(${require('@/assets/images/hand-holding-smartphone.png')})`}"-->
<!--                      style="    display: block;background-size: contain;height: 100%;background-repeat: no-repeat;background-position: 180px -16px;transform: scaleX(-1) rotate(18deg);"></span>-->
<!--                <transition name="fade" mode="out-in">-->
                  <picture>
                    <source type="image/avif" :srcset="require('@/assets/images/optimized/bookings-700.avif')">
                    <img :key="1" :src="bookingScreens[0]" alt="Bookings screenshot" class="shot-img" width="704" height="704" loading="lazy">
                  </picture>
                  <!--                  <span :key="bookingIndex" :style="{ backgroundImage: `url(${bookingScreens[bookingIndex]})`}" class="shot-img"></span>-->
<!--                </transition>-->
              </div>

              <!-- Controls -->
              <!--              <button type="button" class="carousel-btn prev" @click="prevBooking" aria-label="Previous screenshot">-->
              <!--                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>-->
              <!--              </button>-->
              <!--              <button type="button" class="carousel-btn next" @click="nextBooking" aria-label="Next screenshot">-->
              <!--                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>-->
              <!--              </button>-->

              <!-- Dots -->
<!--              <div class="dots">-->
<!--                <button v-for="(s,i) in bookingScreens" :key="'dot-'+i" class="dot" :class="{ active: i === bookingIndex }" @click="goBooking(i)" :aria-label="`Go to screenshot ${i+1}`"></button>-->
<!--              </div>-->
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Built for your studio (photo cards) -->
    <section class="container py-5" aria-labelledby="solutions-heading">
      <div class="text-center pb-4">
        <h2 id="solutions-heading" class="h2 fw-bold reveal">{{ $t('solutions.heading') }}</h2>
        <p class="text-secondary reveal mb-0">{{ $t('solutions.subheading') }}</p>
      </div>
      <div class="row g-3">
        <div class="col-md-6 col-xl-3">
          <a :href="seoUrl('/gym-management-software/')" @click="navTo($event, seoUrl('/gym-management-software/'))" class="card card-pro solution-link solution-link--photo h-100 text-decoration-none text-dark reveal">
            <div class="solution-link__media"><picture><source type="image/avif" srcset="/photos/optimized/gym-management.avif"><img src="/photos/gym-management.jpg" :alt="$t('solutions.gym.title')" width="800" height="536" loading="lazy"></picture></div>
            <div class="card-body">
              <h3 class="h5 fw-semibold mb-2">{{ $t('solutions.gym.title') }}</h3>
              <p class="text-secondary small mb-0">{{ $t('solutions.gym.text') }}</p>
              <div class="solution-link__cta">
                <span>{{ $t('solutions.cta') }}</span>
                <span aria-hidden="true" class="solution-link__arrow">→</span>
              </div>
            </div>
          </a>
        </div>
        <div class="col-md-6 col-xl-3">
          <a :href="seoUrl('/fitness-studio-software/')" @click="navTo($event, seoUrl('/fitness-studio-software/'))" class="card card-pro solution-link solution-link--photo h-100 text-decoration-none text-dark reveal">
            <div class="solution-link__media"><picture><source type="image/avif" srcset="/photos/optimized/fitness-studio.avif"><img src="/photos/fitness-studio.jpg" :alt="$t('solutions.fitness.title')" width="800" height="536" loading="lazy"></picture></div>
            <div class="card-body">
              <h3 class="h5 fw-semibold mb-2">{{ $t('solutions.fitness.title') }}</h3>
              <p class="text-secondary small mb-0">{{ $t('solutions.fitness.text') }}</p>
              <div class="solution-link__cta">
                <span>{{ $t('solutions.cta') }}</span>
                <span aria-hidden="true" class="solution-link__arrow">→</span>
              </div>
            </div>
          </a>
        </div>
        <div class="col-md-6 col-xl-3">
          <a :href="seoUrl('/pilates-studio-software/')" @click="navTo($event, seoUrl('/pilates-studio-software/'))" class="card card-pro solution-link solution-link--photo h-100 text-decoration-none text-dark reveal">
            <div class="solution-link__media"><picture><source type="image/avif" srcset="/photos/optimized/pilates-studio.avif"><img src="/photos/pilates-studio.jpg" :alt="$t('solutions.pilates.title')" width="800" height="536" loading="lazy"></picture></div>
            <div class="card-body">
              <h3 class="h5 fw-semibold mb-2">{{ $t('solutions.pilates.title') }}</h3>
              <p class="text-secondary small mb-0">{{ $t('solutions.pilates.text') }}</p>
              <div class="solution-link__cta">
                <span>{{ $t('solutions.cta') }}</span>
                <span aria-hidden="true" class="solution-link__arrow">→</span>
              </div>
            </div>
          </a>
        </div>
        <div class="col-md-6 col-xl-3">
          <a :href="seoUrl('/yoga-studio-software/')" @click="navTo($event, seoUrl('/yoga-studio-software/'))" class="card card-pro solution-link solution-link--photo h-100 text-decoration-none text-dark reveal">
            <div class="solution-link__media"><picture><source type="image/avif" srcset="/photos/optimized/yoga-studio.avif"><img src="/photos/yoga-studio.jpg" :alt="$t('solutions.yoga.title')" width="800" height="536" loading="lazy"></picture></div>
            <div class="card-body">
              <h3 class="h5 fw-semibold mb-2">{{ $t('solutions.yoga.title') }}</h3>
              <p class="text-secondary small mb-0">{{ $t('solutions.yoga.text') }}</p>
              <div class="solution-link__cta">
                <span>{{ $t('solutions.cta') }}</span>
                <span aria-hidden="true" class="solution-link__arrow">→</span>
              </div>
            </div>
          </a>
        </div>
      </div>
    </section>

    <!-- App Showcase -->
    <section id="showcase" class="showcase-section container-fluid px-0 py-5">
      <div class="showcase-heading container reveal">
        <p class="showcase-eyebrow">{{ $t('showcase.eyebrow') }}</p>
        <h2 class="showcase-title">{{ $t('showcase.heading') }}</h2>
        <p class="showcase-subheading">{{ $t('showcase.subheading') }}</p>
      </div>
      <div class="showcase-viewport reveal d2">
        <div
          class="showcase-strip"
          ref="showcaseStrip"
          @mouseenter="stopShowcaseAutoplay"
          @mouseleave="startShowcaseAutoplay"
          @focusin="stopShowcaseAutoplay"
          @focusout="startShowcaseAutoplay"
        >
          <div class="showcase-track">
            <article
              v-for="(screen,i) in showcaseScreens"
              :key="screen.id"
              class="shot-item"
              :class="[`shot-item--${screen.format}`, { active: i === showcaseIndex }]"
              @click="centerShowcase(i)"
            >
              <div class="showcase-story__visual">
                <span class="showcase-product-pill">{{ $t(screen.product) }}</span>
                <div
                  v-if="screen.panels"
                  class="showcase-admin-collage"
                  :class="`showcase-admin-collage--${screen.composition}`"
                  role="img"
                  :aria-label="$t(screen.alt)"
                >
                  <picture
                    v-for="panel in screen.panels"
                    :key="panel.id"
                    class="showcase-admin-panel"
                    :class="`showcase-admin-panel--${panel.id}`"
                    aria-hidden="true"
                  >
                    <source v-if="panel.avif" type="image/avif" :srcset="panel.avif">
                    <img
                      :src="panel.fallback"
                      alt=""
                      :width="panel.width"
                      :height="panel.height"
                      loading="lazy"
                    >
                  </picture>
                </div>
                <picture v-else>
                  <source type="image/avif" :srcset="screen.avif">
                  <img
                    :src="screen.fallback"
                    :alt="$t(screen.alt)"
                    class="shot-img"
                    :width="screen.width"
                    :height="screen.height"
                    loading="lazy"
                  >
                </picture>
              </div>
              <div class="showcase-story__copy">
                <span class="showcase-story__number">{{ String(i + 1).padStart(2, '0') }}</span>
                <div>
                  <p class="showcase-story__kicker">{{ $t(screen.kicker) }}</p>
                  <h3>{{ $t(screen.title) }}</h3>
                  <p>{{ $t(screen.text) }}</p>
                </div>
              </div>
            </article>
          </div>
        </div>
        <div class="showcase-controls container">
          <div class="showcase-counter" aria-live="polite">
            <span>{{ String(showcaseIndex + 1).padStart(2, '0') }}</span>
            <span aria-hidden="true">/</span>
            <span>{{ String(showcaseScreens.length).padStart(2, '0') }}</span>
          </div>
          <div class="showcase-dots">
            <button
              v-for="(screen,i) in showcaseScreens"
              :key="`showcase-dot-${screen.id}`"
              class="showcase-dot"
              :class="{ active: i === showcaseIndex }"
              @click="goShowcase(i)"
              :aria-label="$t('showcase.goTo', { number: i + 1, title: $t(screen.title) })"
              :aria-current="i === showcaseIndex ? 'true' : undefined"
            ></button>
          </div>
          <div class="showcase-arrows">
            <button type="button" class="showcase-arrow" @click="prevShowcase" :aria-label="$t('showcase.previous')">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
            <button type="button" class="showcase-arrow" @click="nextShowcase" :aria-label="$t('showcase.next')">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Studio Types -->
<!--    <section id="studio-types" class="container py-5">-->
<!--      <h2 class="h2 fw-bold text-center reveal">{{ $t('studioTypes.heading') }}</h2>-->
<!--      <p class="text-center text-secondary reveal d1">{{ $t('studioTypes.subheading') }}</p>-->
<!--      <div class="studio-masonry reveal d2">-->
<!--        <div v-for="(s, i) in studioTypes" :key="'stype-'+i" class="studio-card">-->
<!--          <div class="card card-pro h-100 text-dark">-->
<!--            <div class="card-body d-flex align-items-start gap-3">-->
<!--              <span class="" v-html="s.icon" aria-hidden="true"></span>-->
<!--              <div>-->
<!--                <div class="fw-semibold">{{ $t(s.t) }}</div>-->
<!--                <div class="text-secondary small">{{ $t(s.d) }}</div>-->
<!--              </div>-->
<!--            </div>-->
<!--          </div>-->
<!--        </div>-->
<!--      </div>-->
<!--    </section>-->

    <!-- Feature canvases -->
    <section id="feature-canvases" class="container py-5">
      <div class="row g-4 align-items-center mb-3">
        <div class="col-lg-6">
          <p class="badge text-bg-light text-uppercase fw-semibold small px-3 py-2 rounded-pill shadow-soft">{{ $t('featureCanvases.badge') }}</p>
          <h2 class="h2 fw-bold mt-2 mb-2 reveal">{{ $t('featureCanvases.heading') }}</h2>
          <p class="text-secondary reveal d1">
            {{ $t('featureCanvases.description') }}
          </p>
        </div>
        <div class="col-lg-6">
          <div class="d-flex flex-wrap gap-2 justify-content-lg-end">
            <span class="fc-pill fc-pill--app">{{ $t('featureCanvases.labels.app') }}</span>
            <span class="fc-pill fc-pill--platform">{{ $t('featureCanvases.labels.platform') }}</span>
          </div>
        </div>
      </div>

      <div class="feature-canvas-grid">
        <article
          v-for="(canvas, idx) in homepageCanvases"
          :key="canvas.id || idx"
          class="feature-canvas reveal"
          :class="`feature-canvas--${canvas.palette || 'mint'}`"
        >
          <div class="feature-canvas__header d-flex justify-content-between align-items-center">
            <span class="fc-pill" :class="canvas.label === 'app' ? 'fc-pill--app' : 'fc-pill--platform'">
              {{ $t(canvas.label === 'app' ? 'featureCanvases.labels.app' : 'featureCanvases.labels.platform') }}
            </span>
<!--            <span class="feature-canvas__index">0{{ idx + 1 }}</span>-->
          </div>
          <h3 class="feature-canvas__title">{{ $t(canvas.title) }}</h3>
          <p class="feature-canvas__copy text-secondary mb-3">{{ $t(canvas.body) }}</p>
          <div class="feature-canvas__body" :class="{ 'feature-canvas__body--no-media': !canvas.media }">
            <ul class="feature-canvas__list">
              <li v-for="point in canvas.bullets" :key="point">
                <span class="fc-bullet-dot" aria-hidden="true"></span>
                <span>{{ $t(point) }}</span>
              </li>
            </ul>
            <div class="feature-canvas__media" :class="canvas.media.class" v-if="canvas.media">
              <template v-if="canvas.media.type === 'image'">
                <img :src="canvas.media.src" :alt="$t(canvas.media.alt || 'featureCanvases.media.placeholder')" :width="canvas.media.width || 350" :height="canvas.media.height || 350" loading="lazy">
              </template>
              <template v-else-if="canvas.media.type === 'video'">
                <div class="feature-canvas__video">
                  <div class="feature-canvas__video-thumb" :style="canvas.media.poster ? { backgroundImage: `url(${canvas.media.poster})` } : {}"></div>
                  <div class="feature-canvas__video-note">
                    <small>{{ $t(canvas.media.placeholder || 'featureCanvases.media.placeholder') }}</small>
                  </div>
                </div>
              </template>
            </div>
            <div v-else class="feature-canvas__media feature-canvas__media--placeholder">
              <div class="feature-canvas__video-thumb feature-canvas__video-thumb--ghost"></div>
              <div class="feature-canvas__video-note">
                <small>{{ $t('featureCanvases.media.placeholder') }}</small>
              </div>
            </div>
          </div>
        </article>
      </div>
    </section>

    <!-- How it works (simple inline steps) -->
    <section id="how" class="container py-5">
      <div class="card how-card reveal">
        <div class="card-body p-4 p-lg-5">
          <div class="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3">
            <div>
              <p class="badge text-bg-light text-uppercase fw-semibold small px-3 py-2 rounded-pill shadow-soft">{{ $t('how.badge') }}</p>
              <h2 class="h2 fw-bold mt-2 mb-1">{{ $t('how.heading') }}</h2>
              <p class="text-secondary mb-0">{{ $t('how.subheading') }}</p>
            </div>
          </div>

          <div class="row g-3 g-lg-4 mt-4">
            <div v-for="(step, idx) in how" :key="'how-'+idx" class="col-lg-4">
              <div class="how-step h-100">
                <div class="how-step__badge">0{{ idx + 1 }}</div>
                <h3 class="h5 fw-semibold mb-2">{{ $t(step.title) }}</h3>
                <p class="text-secondary small mb-0">{{ $t(step.text) }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing -->
    <section id="pricing" class="py-5" :style="pricingSectionStyle">

      <!-- Pricing Message -->
      <div class="card card-pro reveal mt-3 mb-4">
        <div class="card-body p-4">
          <h2 class="h2 fw-bold text-center reveal">{{ $t('pricing.heading') }}</h2>
          <p class="text-center text-secondary reveal d1 pb-4">{{ $t('pricing.subheading') }}</p>
          <div class="text-center">
            <p class="fs-5 fw-semibold mb-3">{{ $t('pricing.contact_message') }}</p>
            <a href="#contactForm" class="btn btn-outline-glass btn-lg btn-press btn-glow">{{ $t('pricing.contact_cta') }}</a>
          </div>

          <!-- Pricing Calculator (temporarily disabled) -->
<!--          <h3 class="h5 mb-3">{{ $t('pricing.calculator') }}</h3>-->
<!--          <div class="row g-3">-->
<!--            <div class="col-12">-->
<!--              <label class="form-label small">{{ $t('pricing.avg_spots') }}</label>-->
<!--              <input style="width: 150px;" type="number" min="0" class="form-control" v-model.number="calc.spotsPerWeek" placeholder="e.g. 250">-->
<!--              <div class="form-text text-secondary small">{{ $t('pricing.avg_spots_example') }}</div>-->
<!--            </div>-->
<!--            <div class="col-sm-6 col-md-4">-->
<!--              <label class="form-label small">{{ $t('pricing.active_trainers') }}</label>-->
<!--              <input type="number" min="0" class="form-control" v-model.number="calc.trainers" placeholder="e.g. 5">-->
<!--            </div>-->

<!--            &lt;!&ndash; Adjustable settings &ndash;&gt;-->
<!--            <div class="col-12">-->
<!--              <details>-->
<!--                <summary class="small text-secondary">Adjust settings</summary>-->
<!--                <div class="row g-3 mt-1">-->
<!--                  <div class="col-sm-6 col-md-3">-->
<!--                    <label class="form-label small">Base fee (€)</label>-->
<!--                    <input type="number" min="0" step="0.01" class="form-control" v-model.number="calc.baseFee">-->
<!--                  </div>-->
<!--                  <div class="col-sm-6 col-md-3">-->
<!--                    <label class="form-label small">Price per Spot (€)</label>-->
<!--                    <input type="number" min="0" step="0.01" class="form-control" v-model.number="calc.pricePerSpot">-->
<!--                  </div>-->
<!--                  <div class="col-sm-6 col-md-3">-->
<!--                    <label class="form-label small">Price per Trainer (€)</label>-->
<!--                    <input type="number" min="0" step="0.01" class="form-control" v-model.number="calc.pricePerTrainer">-->
<!--                  </div>-->
<!--                  <div class="col-sm-6 col-md-3">-->
<!--                    <label class="form-label small">Round Up To (€)</label>-->
<!--                    <input type="number" min="1" step="1" class="form-control" v-model.number="calc.roundTo">-->
<!--                  </div>-->
<!--                </div>-->
<!--              </details>-->
<!--            </div>-->

            <!-- POS Bar input -->
<!--            <div class="col-sm-6 col-md-4 d-flex align-items-end">-->
<!--              <div class="form-check mt-4 mb-2">-->
<!--                <input class="form-check-input" type="checkbox" id="usePosBar" v-model="calc.usePosBar">-->
<!--                <label class="form-check-label small" for="usePosBar">{{ $t('pricing.use_pos_bar') }}</label>-->
<!--              </div>-->
<!--            </div>-->

<!--            <div class="col-12"><hr class="my-2"></div>-->

<!--            <div v-if="calc.spotsPerWeek !== null && calc.spotsPerWeek !== ''" class="text-end">-->
<!--              <div class="small">{{ $t('pricing.calculated_price') }}</div>-->
<!--              <div class="fs-4 fw-bold price-output">-->
<!--                <span-->
<!--                  v-if="isPriceLoading"-->
<!--                  class="price-loader"-->
<!--                  role="status"-->
<!--                  aria-live="polite"-->
<!--                >-->
<!--                  <span class="price-loader__currency">€</span>-->
<!--                  <span class="price-loader__digits">{{ priceLoaderDigits }}</span>-->
<!--                </span>-->
<!--                <span v-else>-->
<!--                  <template v-if="priceRangeDisplay && priceRangeDisplay.type === 'range'">-->
<!--                    €{{ priceRangeDisplay.min }}–{{ priceRangeDisplay.max }}-->
<!--                  </template>-->
<!--                  <template v-else-if="priceRangeDisplay && priceRangeDisplay.type === 'custom'">-->
<!--                    {{ $t('pricing.custom_pricing') }}-->
<!--                  </template>-->
<!--                  <template v-else>-->
<!--                    ~-->
<!--                  </template>-->
<!--                </span>-->
<!--              </div>-->
<!--            </div>-->



<!--          </div>-->
        </div>
      </div>

<!--      <div class="row g-3 mt-2">-->
<!--        <div class="col-md-4" v-for="p in pricing" :key="p.title">-->
<!--          <div class="card card-pro h-100 reveal" :class="{ 'card-pro&#45;&#45;featured': p.featured }">-->
<!--            <div class="card-body">-->
<!--              <h3 class="h5">{{ p.title }}</h3>-->
<!--              <div class="display-6 fw-bold">{{ p.price }}<span v-if="p.per" class="fs-6 fw-normal text-secondary">/{{ p.per }}</span></div>-->
<!--              <ul class="list-unstyled small mt-3 mb-0">-->
<!--                <li class="d-flex gap-2 align-items-start mb-1" v-for="item in p.items" :key="item">-->
<!--                  <span v-html="icons.check" class="pt-1" aria-hidden="true"></span>-->
<!--                  <span>{{ item }}</span>-->
<!--                </li>-->
<!--              </ul>-->
<!--              <a href="#cta" class="btn w-100 mt-3" :class="p.featured ? 'btn-primary btn-glow' : 'btn-outline-secondary'">{{ p.cta }}</a>-->
<!--            </div>-->
<!--          </div>-->
<!--        </div>-->
<!--      </div>-->
    </section>


    <!-- Testimonials -->
    <section class="container py-5 pb-0">
      <div class="row g-3">
        <div class="col-md-4" v-for="t in testimonials" :key="t.id">
          <div class="card card-quote h-100 reveal">
            <div class="card-body small">
              “{{ $t(t.quoteKey) }}”
              <div class="mt-3 text-secondary">— {{ $t(t.authorKey) }}</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section id="cta" class=" py-5">
      <div class="cta-wrap gradient-emerald text-white reveal"  :style="{ backgroundImage: `url(${require('@/assets/images/gym/gym3.jpeg')})`, borderRadius: '0px', padding: '70px 70px' }" style="background-size: cover;background-position:0px -165px">
        <div class="hero-overlay" style="z-index: -3"></div>
        <div class="pb-3">
          <h3 class="h3 fw-bold mb-1">{{ $t('cta.title') }}</h3>
          <p class="mb-0">{{ $t('cta.subtitle') }}</p>
        </div>
        <div class="d-flex gap-2 pt-3">
          <!--          <a class="btn btn-glass btn-lg btn-press text-white" :href="#contactForm">Request demo</a>-->
          <a class="btn btn-outline-glass btn-lg btn-press" href="#contactForm">{{ $t('cta.request_demo') }}</a>
          <!--          <button class="btn btn-outline-glass btn-lg btn-press" @click="downloadOnePager">Download one‑pager</button>-->
        </div>
      </div>
    </section>

    <!-- Contact form -->
    <contact-section id="contactForm" v-model:form="model" />



    <!-- FAQ -->
    <section id="faq" class="container py-5">
      <h2 class="h2 fw-bold text-center reveal pb-2">{{ $t('faq.heading') }}</h2>
      <div class="row g-3 mt-2">
        <div class="col-md-6" v-for="q in faq" :key="q.qKey">
          <div class="card card-pro h-100 reveal">
            <div class="card-body">
              <h3 class="h6 fw-semibold mb-2">{{ $t(q.qKey) }}</h3>
              <p class="text-secondary small mb-0">{{ $t(q.aKey) }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="border-top py-4 small bg-white">
      <div class="container d-flex flex-column flex-md-row align-items-center justify-content-between gap-3">
        <div class="text-secondary">© {{ new Date().getFullYear() }} PineNative Ltd — {{ $t('footer.copyright') }}</div>
        <div class="d-flex flex-wrap align-items-center gap-3">
          <a href="/privacy.html" class="link-secondary">{{ $t('footer.privacy') }}</a>
          <a href="/terms.html" class="link-secondary ">{{ $t('footer.terms') }}</a>
          <a href="#" @click.prevent="openCookiePrefs" class="link-secondary">{{ $t('footer.cookie_prefs') }}</a>
          <a href="#contactForm" class="link-secondary">{{ $t('footer.contact') }}</a>
          <a v-if="contactEmail" :href="'mailto:' + contactEmail" class="link-secondary">{{ contactEmail }}</a>
          <a v-if="contactPhone" :href="contactPhoneHref" class="link-secondary">{{ contactPhone }}</a>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import ContactSection from "@/Pages/ContactUs.vue";
import SiteNav from "@/components/seo/SiteNav.vue";
import seoSlugs from "@/seo/seo-slugs.json";
import { setHead } from "@/utils/seoHead";

const HOME_META = {
  en: {
    title: 'Pine Fitness (PineFitness) | Gym Management Software for Studios',
    description: 'Pine Fitness, branded as PineFitness, is gym management software for studios and gyms. Manage class bookings, memberships, billing, attendance, scheduling, and member apps in one platform.'
  },
  el: {
    title: 'PineFitness | Λογισμικό Διαχείρισης Γυμναστηρίου & Studio',
    description: 'Η PineFitness είναι λογισμικό διαχείρισης για γυμναστήρια και studios. Κρατήσεις μαθημάτων, συνδρομές, χρεώσεις, παρουσίες, προγραμματισμός και εφαρμογές μελών σε μία πλατφόρμα.'
  }
};

export default {
  name: 'LandingPage',
  components: {ContactSection, SiteNav},
  data() {
    return {
      // Language state
      languages: [
        {
          code: 'en',
          label: 'EN',
          name: 'English',
          flag: require('@/assets/images/flag/rect/gb.png'),
          flagAlt: 'United Kingdom flag'
        },
        {
          code: 'el',
          label: 'EL',
          name: 'Ελληνικά',
          flag: require('@/assets/images/flag/rect/gr.png'),
          flagAlt: 'Greece flag'
        }
      ],
      selectedLang: 'en',
      langMenuOpen: false,
      featOpen: false,
      studiosOpen: false,
      model: {
        name: null,
        company: "",
        email: null,
        subject: null,
        description: "",
        phoneNumber: "",
        phoneNumberCode: "+357"
      },
      scrolled: false,
      // Partners/App icons (explicit list)
      // Each item: { name: string, img: string, url?: string, classes?: string|string[] (caption), imgWrapClasses?: string|string[] (around img) }
      logos: [
        {
          name: 'Reload Fitness Studio',
          img: require('@/assets/images/partnerLogos/reload-fitness-studio.svg'),
          url: 'https://reload-fitness.com/',
          classes: 'caption-futura-upper',
        },
        {
          name: 'West 34° Hybrid',
          img: require('@/assets/images/partnerLogos/west34Logo.webp'),
          url: 'https://www.instagram.com/west34hybrid',
          classes: 'caption-futura-upper',
        },
        {
          name: 'GG Clinical Pilates',
          img: require('@/assets/images/partnerLogos/gg-clinical-pilates.png'),
          url: 'https://www.facebook.com/p/GG-Physiowork-100034989713831/',
          classes: 'caption-futura-upper',
        },
        {
          name: 'Lola c.Pilates',
          img: require('@/assets/images/partnerLogos/lola-c-pilates.png'),
          url: 'https://www.instagram.com/lolacpilates/',
          classes: 'caption-futura-upper',
        },
        {
          name: 'Pilates Reformer Studio',
          img: require('@/assets/images/partnerLogos/pilates-reformer-studio.png'),
          url: 'https://www.instagram.com/pilates.reformer.studio/',
          classes: 'caption-futura-upper',
        },
        {
          name: 'Exhale Pilates Boutique',
          img: require('@/assets/images/partnerLogos/exhale-pilates-boutique.png'),
          url: 'https://exhalepilates.cy/',
          classes: 'caption-futura-upper',
        },
        {
          name: 'Vo2 Fitness Center',
          img: require('@/assets/images/partnerLogos/vo2-fitness-center.png'),
          url: 'https://vo2fitnesscenter.com/',
          classes: 'caption-futura-upper',
        },
        {
          name: 'Marlen Pilates',
          img: require('@/assets/images/partnerLogos/marlen.webp'),
          url: 'https://marlenpilates.cy/',
          classes: 'caption-futura-upper',
        },
        {
          name: 'Posture Physical Therapy Centre',
          img: require('@/assets/images/partnerLogos/posture.jpg'),
          url: 'https://www.instagram.com/posture_physicaltherapy_centre',
          classes: 'caption-futura-upper',
        },
        {
          name: 'The Blissful Hour',
          img: require('@/assets/images/partnerLogos/bliss.jpg'),
          url: 'https://www.instagram.com/theblissfulhour',
          classes: 'caption-futura-upper',
        }
        // Add more partners here
        // { name: 'Your Partner', img: require('@/assets/images/partnerLogos/your-partner.svg'), classes: 'your-custom-class' }
      ],
      animateLogos: true,
      productAreas: [
        {
          href: '/class-booking-software/',
          title: 'productAreas.booking.title',
          text: 'productAreas.booking.text',
          icon: `<svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'><rect x='3' y='4' width='18' height='17' rx='3' stroke='currentColor' stroke-width='1.7'/><path d='M7 2v4M17 2v4M3 9h18M8 14h3M13 14h3M8 18h3' stroke='currentColor' stroke-width='1.7' stroke-linecap='round'/></svg>`
        },
        {
          href: '/gym-membership-billing-software/',
          title: 'productAreas.membership.title',
          text: 'productAreas.membership.text',
          icon: `<svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'><rect x='2.5' y='5' width='19' height='14' rx='3' stroke='currentColor' stroke-width='1.7'/><path d='M2.5 9.5h19M6 15h4' stroke='currentColor' stroke-width='1.7' stroke-linecap='round'/></svg>`
        },
        {
          href: '/personal-training-software/',
          title: 'productAreas.coaching.title',
          text: 'productAreas.coaching.text',
          icon: `<svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M3 10v4M6 8v8M18 8v8M21 10v4M6 12h12' stroke='currentColor' stroke-width='1.7' stroke-linecap='round'/><path d='M10 6h4M10 18h4' stroke='currentColor' stroke-width='1.7' stroke-linecap='round'/></svg>`
        }
      ],
      capabilities: [
        {
          href: '/fitness-marketing-software/',
          title: 'capabilities.marketing.title',
          text: 'capabilities.marketing.text',
          icon: `<svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M3 11v2a1 1 0 001 1h2l3.5 3.5V7.5L6 11H4a1 1 0 00-1 0z' stroke='currentColor' stroke-width='1.7' stroke-linejoin='round'/><path d='M14 8a5 5 0 010 8M17 5a9 9 0 010 14' stroke='currentColor' stroke-width='1.7' stroke-linecap='round'/></svg>`
        },
        {
          href: '/online-class-booking-software/',
          title: 'capabilities.online.title',
          text: 'capabilities.online.text',
          icon: `<svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'><rect x='2.5' y='4' width='19' height='13' rx='2' stroke='currentColor' stroke-width='1.7'/><path d='M8 21h8M12 17v4' stroke='currentColor' stroke-width='1.7' stroke-linecap='round'/><path d='M10.5 8.5l4 2.5-4 2.5v-5z' stroke='currentColor' stroke-width='1.6' stroke-linejoin='round'/></svg>`
        },
        {
          href: '/corporate-wellness-software/',
          title: 'capabilities.corporate.title',
          text: 'capabilities.corporate.text',
          icon: `<svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'><rect x='3' y='8' width='8' height='12' rx='1' stroke='currentColor' stroke-width='1.7'/><path d='M11 12h7a1 1 0 011 1v7H11' stroke='currentColor' stroke-width='1.7' stroke-linejoin='round'/><path d='M6 11v0M6 14v0M6 17v0M15 15v0M15 18v0' stroke='currentColor' stroke-width='1.7' stroke-linecap='round'/></svg>`
        }
      ],
      featureCanvases: [
        {
          id: 'app-profiles',
          label: 'app',
          title: 'featureCanvases.items.appProfiles.title',
          body: 'featureCanvases.items.appProfiles.body',
          bullets: [
            'featureCanvases.items.appProfiles.bullets.0',
            'featureCanvases.items.appProfiles.bullets.1',
            'featureCanvases.items.appProfiles.bullets.2'
          ],
          media: {
            type: 'image',
            src: require('@/assets/images/featureCanvas/switch.png'),
            alt: 'featureCanvases.items.appProfiles.mediaAlt'
          },
          palette: 'mint'
        },
        {
          id: 'app-schedule',
          label: 'app',
          title: 'featureCanvases.items.appSchedule.title',
          body: 'featureCanvases.items.appSchedule.body',
          bullets: [
            'featureCanvases.items.appSchedule.bullets.0',
            'featureCanvases.items.appSchedule.bullets.1',
            'featureCanvases.items.appSchedule.bullets.2'
          ],
          media: {
            type: 'image',
            src: require('@/assets/images/featureCanvas/1.png'),
            alt: 'featureCanvases.items.appSchedule.mediaAlt'
          },
          palette: 'indigo'
        },
        {
          id: 'app-commerce',
          label: 'app',
          title: 'featureCanvases.items.appCommerce.title',
          body: 'featureCanvases.items.appCommerce.body',
          bullets: [
            'featureCanvases.items.appCommerce.bullets.0',
            'featureCanvases.items.appCommerce.bullets.1',
            'featureCanvases.items.appCommerce.bullets.2'
          ],
          media: {
            type: 'image',
            src: require('@/assets/images/featureCanvas/shop.png'),
            height: 233,
            alt: 'featureCanvases.items.appCommerce.mediaAlt'
          },
          palette: 'sunrise'
        },
        {
          id: 'app-onboarding',
          label: 'app',
          title: 'featureCanvases.items.appOnboarding.title',
          body: 'featureCanvases.items.appOnboarding.body',
          bullets: [
            'featureCanvases.items.appOnboarding.bullets.0',
            'featureCanvases.items.appOnboarding.bullets.1',
            'featureCanvases.items.appOnboarding.bullets.2'
          ],
          media: {
            type: 'image',
            alt: 'featureCanvases.items.appOnboarding.mediaAlt',
            class: "auto-flex",
            src: require('@/assets/images/featureCanvas/registration.png')
          },
          palette: 'sand'
        },
        {
          id: 'platform-desk',
          label: 'platform',
          title: 'featureCanvases.items.platformDesk.title',
          body: 'featureCanvases.items.platformDesk.body',
          bullets: [
            'featureCanvases.items.platformDesk.bullets.0',
            'featureCanvases.items.platformDesk.bullets.1',
            'featureCanvases.items.platformDesk.bullets.2'
          ],
          media: {
            type: 'image',
            src: require('@/assets/images/featureCanvas/2.png'),
            alt: 'featureCanvases.items.platformDesk.mediaAlt'
          },
          palette: 'slate'
        },
        {
          id: 'platform-classes',
          label: 'platform',
          title: 'featureCanvases.items.platformClasses.title',
          body: 'featureCanvases.items.platformClasses.body',
          bullets: [
            'featureCanvases.items.platformClasses.bullets.0',
            'featureCanvases.items.platformClasses.bullets.1'
          ],
          media: {
            type: 'image',
            src: require('@/assets/images/featureCanvas/guided.png'),
            alt: 'featureCanvases.items.platformClasses.mediaAlt'
          },
          palette: 'aqua'
        },
        {
          id: 'platform-crm',
          label: 'platform',
          title: 'featureCanvases.items.platformCrm.title',
          body: 'featureCanvases.items.platformCrm.body',
          bullets: [
            'featureCanvases.items.platformCrm.bullets.0',
            'featureCanvases.items.platformCrm.bullets.1'
          ],
          media: {
            type: 'image',
            src: require('@/assets/images/featureCanvas/crm.png'),
            height: 233,
            alt: 'featureCanvases.items.platformCrm.mediaAlt'
          },
          palette: 'lavender'
        },
        {
          id: 'platform-sales',
          label: 'platform',
          title: 'featureCanvases.items.platformSales.title',
          body: 'featureCanvases.items.platformSales.body',
          bullets: [
            'featureCanvases.items.platformSales.bullets.0',
            'featureCanvases.items.platformSales.bullets.1'
          ],
          media: {
            type: 'image',
            src: require('@/assets/images/featureCanvas/sync.png'),
            alt: 'featureCanvases.items.platformSales.mediaAlt'
          },
          palette: 'peach'
        },
        {
          id: 'platform-reports',
          label: 'platform',
          title: 'featureCanvases.items.platformReports.title',
          body: 'featureCanvases.items.platformReports.body',
          bullets: [
            'featureCanvases.items.platformReports.bullets.0',
            'featureCanvases.items.platformReports.bullets.1'
          ],
          media: {
            type: 'image',
            src: require('@/assets/images/featureCanvas/reporting.png'),
            alt: 'featureCanvases.items.platformReports.mediaAlt'
          },
          palette: 'night'
        }
      ],
      // Icons for studio types (24x24, stroke=currentColor)
      studioTypeIcons: {
        pilates: `<svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'><rect x='3' y='15' width='18' height='4' rx='2' stroke='currentColor' stroke-width='1.6'/><circle cx='8' cy='10' r='2' stroke='currentColor' stroke-width='1.6'/></svg>`,
        yoga: `<svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M12 4c2 3 2 5 0 8-2-3-2-5 0-8z' stroke='currentColor' stroke-width='1.6'/><path d='M6 14c2-1 4 0 6 2 2-2 4-3 6-2' stroke='currentColor' stroke-width='1.6' stroke-linecap='round'/></svg>`,
        dumbbell: `<svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'><rect x='2' y='9' width='4' height='6' rx='1' stroke='currentColor' stroke-width='1.6'/><rect x='18' y='9' width='4' height='6' rx='1' stroke='currentColor' stroke-width='1.6'/><path d='M6 12h12' stroke='currentColor' stroke-width='1.6' stroke-linecap='round'/></svg>`,
        kettlebell: `<svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'><circle cx='12' cy='14' r='6' stroke='currentColor' stroke-width='1.6'/><path d='M8 8a4 4 0 018 0' stroke='currentColor' stroke-width='1.6' stroke-linecap='round'/></svg>`,
        bag: `<svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M12 3v3' stroke='currentColor' stroke-width='1.6' stroke-linecap='round'/><rect x='9' y='6' width='6' height='12' rx='2' stroke='currentColor' stroke-width='1.6'/></svg>`,
        belt: `<svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'><rect x='3' y='10' width='18' height='4' rx='2' stroke='currentColor' stroke-width='1.6'/><path d='M12 10v4' stroke='currentColor' stroke-width='1.6'/></svg>`,
        wheel: `<svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'><circle cx='12' cy='12' r='6' stroke='currentColor' stroke-width='1.6'/><path d='M12 6v12M6 12h12M8.2 8.2l7.6 7.6M15.8 8.2l-7.6 7.6' stroke='currentColor' stroke-width='1.4' stroke-linecap='round'/></svg>`,
        music: `<svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M14 3v10a3 3 0 11-2-2.83V7l8-2v6a3 3 0 11-2-2.83' stroke='currentColor' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/></svg>`,
        straps: `<svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M8 4v10M16 4v10' stroke='currentColor' stroke-width='1.6' stroke-linecap='round'/><circle cx='8' cy='18' r='2' stroke='currentColor' stroke-width='1.6'/><circle cx='16' cy='18' r='2' stroke='currentColor' stroke-width='1.6'/></svg>`,
        oar: `<svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M4 16c2 1 4 1 6 0 2 1 4 1 6 0' stroke='currentColor' stroke-width='1.6' stroke-linecap='round'/><path d='M20 6l-6 6' stroke='currentColor' stroke-width='1.6' stroke-linecap='round'/><rect x='18' y='4' width='4' height='4' rx='1' stroke='currentColor' stroke-width='1.6'/></svg>`,
        stretch: `<svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M4 16c4-6 12-6 16 0' stroke='currentColor' stroke-width='1.6' stroke-linecap='round'/></svg>`,
        pole: `<svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M12 3v18' stroke='currentColor' stroke-width='1.6'/><circle cx='12' cy='7' r='2' stroke='currentColor' stroke-width='1.6'/></svg>`,
        sun: `<svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'><circle cx='12' cy='12' r='4' stroke='currentColor' stroke-width='1.6'/><path d='M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2' stroke='currentColor' stroke-width='1.6' stroke-linecap='round'/></svg>`,
        leaf: `<svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M20 4C10 4 4 10 4 20c10 0 16-6 16-16z' stroke='currentColor' stroke-width='1.6'/><path d='M8 16c2-3 5-6 8-8' stroke='currentColor' stroke-width='1.6' stroke-linecap='round'/></svg>`,
        cross: `<svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M10 4h4v6h6v4h-6v6h-4v-6H4v-4h6V4z' stroke='currentColor' stroke-width='1.6' stroke-linejoin='round'/></svg>`,
        star: `<svg width='20' height='20' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M12 3l2.6 5.4 6 .9-4.3 4.2 1 6-5.3-2.8-5.3 2.8 1-6L3.4 9.3l6-.9L12 3z' stroke='currentColor' stroke-width='1.6' stroke-linejoin='round'/></svg>`
      },
      studioTypes: [
        { t: 'studioTypes.items.0.title', d: 'studioTypes.items.0.desc', icon: null },
        { t: 'studioTypes.items.1.title', d: 'studioTypes.items.1.desc', icon: null },
        { t: 'studioTypes.items.2.title', d: 'studioTypes.items.2.desc', icon: null },
        { t: 'studioTypes.items.3.title', d: 'studioTypes.items.3.desc', icon: null },
        { t: 'studioTypes.items.4.title', d: 'studioTypes.items.4.desc', icon: null },
        { t: 'studioTypes.items.5.title', d: 'studioTypes.items.5.desc', icon: null },
        { t: 'studioTypes.items.6.title', d: 'studioTypes.items.6.desc', icon: null },
        { t: 'studioTypes.items.7.title', d: 'studioTypes.items.7.desc', icon: null },
        { t: 'studioTypes.items.8.title', d: 'studioTypes.items.8.desc', icon: null },
        { t: 'studioTypes.items.9.title', d: 'studioTypes.items.9.desc', icon: null },
        { t: 'studioTypes.items.10.title', d: 'studioTypes.items.10.desc', icon: null },
        { t: 'studioTypes.items.11.title', d: 'studioTypes.items.11.desc', icon: null },
        { t: 'studioTypes.items.12.title', d: 'studioTypes.items.12.desc', icon: null },
        { t: 'studioTypes.items.13.title', d: 'studioTypes.items.13.desc', icon: null },
        { t: 'studioTypes.items.14.title', d: 'studioTypes.items.14.desc', icon: null },
        { t: 'studioTypes.items.15.title', d: 'studioTypes.items.15.desc', icon: null },
        { t: 'studioTypes.items.16.title', d: 'studioTypes.items.16.desc', icon: null },
        { t: 'studioTypes.items.17.title', d: 'studioTypes.items.17.desc', icon: null }
      ],
      how: [
        { title: 'how.steps.setup.title', text: 'how.steps.setup.text' },
        { title: 'how.steps.publish.title', text: 'how.steps.publish.text' },
        { title: 'how.steps.book.title', text: 'how.steps.book.text' }
      ],
      pricing: [
        { title: 'pricing.plans.starter.title', per: 'pricing.plans.starter.per', items: ['pricing.plans.starter.items.0','pricing.plans.starter.items.1','pricing.plans.starter.items.2','pricing.plans.starter.items.3'], cta: 'pricing.plans.starter.cta', featured: false },
        { title: 'pricing.plans.growth.title', per: 'pricing.plans.growth.per', items: ['pricing.plans.growth.items.0','pricing.plans.growth.items.1','pricing.plans.growth.items.2','pricing.plans.growth.items.3'], cta: 'pricing.plans.growth.cta', featured: true },
        { title: 'pricing.plans.enterprise.title', per: 'pricing.plans.enterprise.per', items: ['pricing.plans.enterprise.items.0','pricing.plans.enterprise.items.1','pricing.plans.enterprise.items.2','pricing.plans.enterprise.items.3'], cta: 'pricing.plans.enterprise.cta', featured: false }
      ],
      testimonials: [
        { id: 1, quoteKey: 'testimonials.0.quote', authorKey: 'testimonials.0.author' },
        { id: 2, quoteKey: 'testimonials.1.quote', authorKey: 'testimonials.1.author' },
        { id: 3, quoteKey: 'testimonials.2.quote', authorKey: 'testimonials.2.author' }
      ],
      showcaseScreens: [
        {
          id: 'member-home',
          format: 'phone',
          avif: require('@/assets/images/showcase-v2/member-home.avif'),
          fallback: require('@/assets/images/showcase-v2/member-home.jpg'),
          width: 660,
          height: 1434,
          product: 'showcase.items.memberHome.product',
          kicker: 'showcase.items.memberHome.kicker',
          title: 'showcase.items.memberHome.title',
          text: 'showcase.items.memberHome.text',
          alt: 'showcase.items.memberHome.alt'
        },
        {
          id: 'spot-selection',
          format: 'phone',
          avif: require('@/assets/images/showcase-v2/spot-selection.avif'),
          fallback: require('@/assets/images/showcase-v2/spot-selection.jpg'),
          width: 660,
          height: 1434,
          product: 'showcase.items.spotSelection.product',
          kicker: 'showcase.items.spotSelection.kicker',
          title: 'showcase.items.spotSelection.title',
          text: 'showcase.items.spotSelection.text',
          alt: 'showcase.items.spotSelection.alt'
        },
        {
          id: 'member-checkout',
          format: 'phone',
          avif: require('@/assets/images/showcase-v2/member-checkout.avif'),
          fallback: require('@/assets/images/showcase-v2/member-checkout.jpg'),
          width: 660,
          height: 1434,
          product: 'showcase.items.memberCheckout.product',
          kicker: 'showcase.items.memberCheckout.kicker',
          title: 'showcase.items.memberCheckout.title',
          text: 'showcase.items.memberCheckout.text',
          alt: 'showcase.items.memberCheckout.alt'
        },
        {
          id: 'sale-summary',
          format: 'admin-collage',
          composition: 'sales',
          panels: [
            {
              id: 'sale-details',
              avif: require('@/assets/images/showcase-v2/sale-details-focus.avif'),
              fallback: require('@/assets/images/showcase-v2/sale-details-focus.jpg'),
              width: 770,
              height: 680
            },
            {
              id: 'sale-documents',
              fallback: require('@/assets/images/showcase-v2/sale-documents-focus.jpg'),
              width: 770,
              height: 450
            },
            {
              id: 'sale-summary',
              avif: require('@/assets/images/showcase-v2/sale-summary-focus.avif'),
              fallback: require('@/assets/images/showcase-v2/sale-summary-focus.jpg'),
              width: 770,
              height: 900
            }
          ],
          product: 'showcase.items.saleSummary.product',
          kicker: 'showcase.items.saleSummary.kicker',
          title: 'showcase.items.saleSummary.title',
          text: 'showcase.items.saleSummary.text',
          alt: 'showcase.items.saleSummary.alt'
        },
        {
          id: 'staff-schedule',
          format: 'phone',
          avif: require('@/assets/images/showcase-v2/staff-schedule.avif'),
          fallback: require('@/assets/images/showcase-v2/staff-schedule.jpg'),
          width: 660,
          height: 1434,
          product: 'showcase.items.staffSchedule.product',
          kicker: 'showcase.items.staffSchedule.kicker',
          title: 'showcase.items.staffSchedule.title',
          text: 'showcase.items.staffSchedule.text',
          alt: 'showcase.items.staffSchedule.alt'
        },
        {
          id: 'member-insights',
          format: 'admin-collage',
          composition: 'insights',
          panels: [
            {
              id: 'insights-risk',
              fallback: require('@/assets/images/showcase-v2/insights-risk-focus.jpg'),
              width: 620,
              height: 430
            },
            {
              id: 'insights-visits',
              fallback: require('@/assets/images/showcase-v2/insights-visits-flag.jpg'),
              width: 1150,
              height: 130
            },
            {
              id: 'insights-credits',
              fallback: require('@/assets/images/showcase-v2/insights-credits-flag.jpg'),
              width: 1110,
              height: 130
            }
          ],
          product: 'showcase.items.memberInsights.product',
          kicker: 'showcase.items.memberInsights.kicker',
          title: 'showcase.items.memberInsights.title',
          text: 'showcase.items.memberInsights.text',
          alt: 'showcase.items.memberInsights.alt'
        }
      ],
      showcaseIndex: 0,
      bookingScreens: [
        require('@/assets/images/phoneApp/website_slider_2.png')
      ],
      bookingIndex: 0,
      faq: [
        { qKey: 'faq.items.0.q', aKey: 'faq.items.0.a' },
        { qKey: 'faq.items.1.q', aKey: 'faq.items.1.a' },
        { qKey: 'faq.items.2.q', aKey: 'faq.items.2.a' },
        { qKey: 'faq.items.3.q', aKey: 'faq.items.3.a' }
      ],
      icons: {
        check: `<svg width='16' height='16' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'><path d='M20 6L9 17l-5-5' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/></svg>`
      },
      // Calculator defaults and inputs
      calc: {
        baseFee: 200,
        pricePerSpot: 0.15,
        hightPerSpot: 0.13,
        pricePerTrainer: 0,
        roundTo: 50,
        spotsPerWeek: null,
        trainers: null,
        usePosBar: false
      },
      // Pricing calculator loader state
      isPriceLoading: false,
      priceLoaderDigits: '000',
      priceLoaderTimer: null,
      priceLoaderInterval: null
    }
  },
  computed: {
    homepageCanvases() {
      const featured = ['app-schedule', 'app-commerce', 'platform-desk', 'platform-crm']
      return featured.map(id => this.featureCanvases.find(canvas => canvas.id === id)).filter(Boolean)
    },
    currentLang() {
      return this.languages.find(l => l.code === this.selectedLang) || this.languages[0]
    },
    migratedSlugs() {
      return seoSlugs
    },
    contactEmail() {
      return process.env.VUE_APP_CONTACT_EMAIL || 'info@pinenative.com'
    },
    contactPhone() {
      return process.env.VUE_APP_CONTACT_PHONE || '+357 99158398'
    },
    contactPhoneHref() {
      if (!this.contactPhone) return ''
      const digits = String(this.contactPhone).replace(/\s+/g, '')
      return `tel:${digits}`
    },
    // Pricing calculator computed values
    priceRange() {
      const value = Number(this.calc.spotsPerWeek)
      if (!Number.isFinite(value)) return null

      const spots = Math.max(0, value)
      if (spots > 1500) return 'Custom'

      const tiers = [
        { max: 200, base: 200 },
        { max: 300, base: 250 },
        { max: 400, base: 250 },
        { max: 500, base: 300 },
        { max: 600, base: 350 },
        { max: 700, base: 400 },
        { max: 800, base: 450 },
        { max: 900, base: 500 },
        { max: 1000, base: 550 },
        { max: 1100, base: 550 },
        { max: 1200, base: 650 },
        { max: 1300, base: 700 },
        { max: 1400, base: 750 },
        { max: 1500, base: 800 },
      ]

      const tier = tiers.find(t => spots <= t.max)
      if (!tier) return 'Custom'

      const base = tier.base
      const min = Math.max(200, base - 50)
      const max = base + 50
      return { min, max }
    },
    priceRangeDisplay() {
      const range = this.priceRange
      if (!range) return null
      if (range === 'Custom') {
        return { type: 'custom' }
      }
      return {
        type: 'range',
        min: range.min,
        max: range.max
      }
    },
    pricingSectionStyle() {
      return {
        '--pricing-bg-image': `url(${require('@/assets/images/optimized/pricing.avif')})`,
        'padding-bottom': '100px !important'
      }
    },
    animatedLogoDuplicates() {
      return this.animateLogos ? this.logos : []
    }
  },
  watch: {
    'calc.spotsPerWeek'(newVal, oldVal) {
      if (newVal === oldVal) return
      this.startPriceLoaderCycle()
    },
    '$route'() {
      this.applyHomeLocale()
    },
  },
  mounted() {
    // Language is driven by the URL: "/" is English, "/el" is Greek.
    this.applyHomeLocale()

    this._onDocClick = (e) => {
      const root = this.$refs.langSwitcher
      if (root && !root.contains(e.target)) this.langMenuOpen = false
      const feat = this.$refs.featDd
      if (feat && !feat.contains(e.target)) this.featOpen = false
      const studios = this.$refs.studiosDd
      if (studios && !studios.contains(e.target)) this.studiosOpen = false
    }
    document.addEventListener('click', this._onDocClick)

    // Nav elevation on scroll
    const onScroll = () => { this.scrolled = window.scrollY > 10 }
    window.addEventListener('scroll', onScroll, { passive: true })

    // Set hero to fill viewport below header
    const setHeaderH = () => {
      const hdr = document.querySelector('header.sticky-top')
      const h = hdr ? hdr.offsetHeight : 0
      document.documentElement.style.setProperty('--header-h', `${h}px`)
    }
    setHeaderH()
    window.addEventListener('resize', setHeaderH, { passive: true })
    this._onResizeHeader = setHeaderH

    // Scroll‑reveal (skipped during prerender where IntersectionObserver is absent)
    if (typeof IntersectionObserver !== 'undefined') {
      const io = new IntersectionObserver((entries) => {
        entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('reveal-in') })
      }, { threshold: 0.12 })
      document.querySelectorAll('.reveal').forEach(el => io.observe(el))
    }

    // Attach icons to studio types (avoid referencing this in data init)
    const sti = this.studioTypeIcons
    const map = [
      sti.pilates,
      sti.yoga,
      sti.dumbbell,
      sti.kettlebell,
      sti.dumbbell,
      sti.bag,
      sti.belt,
      sti.wheel,
      sti.music,
      sti.dumbbell,
      sti.straps,
      sti.oar,
      sti.stretch,
      sti.pole,
      sti.sun,
      sti.leaf,
      sti.cross,
      sti.star
    ]
    this.studioTypes = this.studioTypes.map((s, i) => ({ ...s, icon: map[i] || sti.dumbbell }))

    // Showcase scroll tracking for active center
    const strip = this.$refs.showcaseStrip
    if (strip) {
      const onScroll = () => {
        const rect = strip.getBoundingClientRect()
        const centerX = strip.scrollLeft + rect.width / 2
        const items = Array.from(strip.querySelectorAll('.shot-item'))
        let bestIdx = this.showcaseIndex
        let bestDist = Infinity
        items.forEach((el, idx) => {
          const c = el.offsetLeft + el.offsetWidth / 2
          const d = Math.abs(c - centerX)
          if (d < bestDist) { bestDist = d; bestIdx = idx }
        })
        this.showcaseIndex = bestIdx
      }
      strip.addEventListener('scroll', onScroll, { passive: true })
      this._showcaseScroll = { el: strip, onScroll }
      // initial center
      this.$nextTick(() => { this.centerShowcase(this.showcaseIndex) })
    }

    const glowBtns = document.querySelectorAll('.btn-glow')
    glowBtns.forEach(btn => {
      btn.addEventListener('pointermove', e => {
        const r = btn.getBoundingClientRect()
        btn.style.setProperty('--x', `${e.clientX - r.left}px`)
        btn.style.setProperty('--y', `${e.clientY - r.top}px`)
      }, { passive: true })
    })
    // Interactive glow for wyget-features
    const wyget = this.$el && this.$el.querySelector('.wyget-features')
    if (wyget) {
      const onMove = (e) => {
        const r = wyget.getBoundingClientRect()
        const x = ((e.clientX - r.left) / r.width) * 100
        const y = ((e.clientY - r.top) / r.height) * 100
        wyget.style.setProperty('--glowX', `${x.toFixed(2)}%`)
        wyget.style.setProperty('--glowY', `${y.toFixed(2)}%`)
      }
      const onLeave = () => {
        wyget.style.setProperty('--glowX', '500%')
        wyget.style.setProperty('--glowY', '500%')
      }
      wyget.addEventListener('pointermove', onMove)
      wyget.addEventListener('pointerleave', onLeave)
      this._wygetGlow = { el: wyget, onMove, onLeave }
    }

    // Interactive glow for all .card-pro
    const cards = this.$el ? Array.from(this.$el.querySelectorAll('.card-pro')) : []
    const cardHandlers = []
    cards.forEach(card => {
      const onMove = (e) => {
        const r = card.getBoundingClientRect()
        const x = ((e.clientX - r.left) / r.width) * 100
        const y = ((e.clientY - r.top) / r.height) * 100
        card.style.setProperty('--glowX', `${x.toFixed(2)}%`)
        card.style.setProperty('--glowY', `${y.toFixed(2)}%`)
      }
      const onLeave = () => {
        card.style.setProperty('--glowX', '50%')
        card.style.setProperty('--glowY', '50%')
      }
      card.addEventListener('pointermove', onMove, { passive: true })
      card.addEventListener('pointerleave', onLeave, { passive: true })
      cardHandlers.push({ card, onMove, onLeave })
    })
    this._cardGlow = cardHandlers
    // Logos are now defined explicitly in data().
    this.startBookingAutoplay()
    this.startShowcaseAutoplay()
  },
  beforeUnmount() {
    if (this._onDocClick) document.removeEventListener('click', this._onDocClick)
    if (this._wygetGlow) {
      const { el, onMove, onLeave } = this._wygetGlow
      el.removeEventListener('pointermove', onMove)
      el.removeEventListener('pointerleave', onLeave)
      this._wygetGlow = null
    }
    if (this._cardGlow && Array.isArray(this._cardGlow)) {
      this._cardGlow.forEach(({ card, onMove, onLeave }) => {
        card.removeEventListener('pointermove', onMove)
        card.removeEventListener('pointerleave', onLeave)
      })
      this._cardGlow = null
    }
    if (this._bookingTimer) {
      clearInterval(this._bookingTimer)
      this._bookingTimer = null
    }
    if (this._showcaseTimer) {
      clearInterval(this._showcaseTimer)
      this._showcaseTimer = null
    }
    if (this._showcaseScroll) {
      const { el, onScroll } = this._showcaseScroll
      el.removeEventListener('scroll', onScroll)
      this._showcaseScroll = null
    }
    if (this._onResizeHeader) {
      window.removeEventListener('resize', this._onResizeHeader)
      this._onResizeHeader = null
    }
    this.clearPriceLoaderTimers()
    this.isPriceLoading = false
  },
  methods: {
    navTo(e, href) {
      // Keep the href for SEO + new-tab/modifier clicks, but do client-side
      // navigation on a normal click so it never triggers a full page reload.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button) return
      e.preventDefault()
      this.$router.push(href)
    },
    applyHomeLocale() {
      // Treat both /el and /el/ as Greek. Hosting may normalize the landing
      // route to a trailing slash, while Vue Router preserves that pathname.
      const path = this.$route.path
      const lang = path === '/el' || path.startsWith('/el/') ? 'el' : 'en'
      if (this.$i18n) this.$i18n.locale = lang
      this.selectedLang = lang
      const m = HOME_META[lang]
      setHead({
        title: m.title,
        description: m.description,
        canonical: lang === 'el' ? 'https://pine.fitness/el/' : 'https://pine.fitness/',
        image: 'https://pine.fitness/logo.png',
        locale: lang,
        alternates: [
          { hreflang: 'en', href: 'https://pine.fitness/' },
          { hreflang: 'el', href: 'https://pine.fitness/el/' },
          { hreflang: 'x-default', href: 'https://pine.fitness/' }
        ],
        jsonLd: {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: this.faq.map(item => ({
            '@type': 'Question',
            name: this.$t(item.qKey),
            acceptedAnswer: {
              '@type': 'Answer',
              text: this.$t(item.aKey)
            }
          }))
        }
      })
    },
    seoUrl(href) {
      // Add the /el prefix only when in Greek AND that page has a Greek route,
      // so links to not-yet-migrated pages stay on their English version.
      const lang = this.$i18n && this.$i18n.locale === 'el' ? 'el' : 'en'
      if (lang !== 'el') return href
      const slug = String(href).replace(/^\//, '').replace(/\/$/, '')
      return this.migratedSlugs.indexOf(slug) !== -1 ? '/el' + href : href
    },
    startPriceLoaderCycle() {
      this.clearPriceLoaderTimers()
      this.isPriceLoading = true
      this.priceLoaderDigits = this.calc.spotsPerWeek === null || this.calc.spotsPerWeek === ''
        ? '000'
        : this.generatePriceLoaderDigits()
      this.priceLoaderInterval = setInterval(() => {
        this.priceLoaderDigits = this.generatePriceLoaderDigits()
      }, 120)
      this.priceLoaderTimer = setTimeout(() => {
        this.isPriceLoading = false
        this.clearPriceLoaderTimers()
      }, 1000)
    },
    clearPriceLoaderTimers() {
      if (this.priceLoaderInterval) {
        clearInterval(this.priceLoaderInterval)
        this.priceLoaderInterval = null
      }
      if (this.priceLoaderTimer) {
        clearTimeout(this.priceLoaderTimer)
        this.priceLoaderTimer = null
      }
    },
    generatePriceLoaderDigits() {
      return Math.floor(100 + Math.random() * 900).toString()
    },
    openCookiePrefs(){
      if (window.pfCookieConsent && typeof window.pfCookieConsent.open === 'function') {
        window.pfCookieConsent.open();
      }
    },
    toggleLangMenu() {
      this.langMenuOpen = !this.langMenuOpen
    },
    selectLang(code) {
      if (!this.languages.some(l => l.code === code)) return
      this.selectedLang = code
      localStorage.setItem('pf_lang', code)
      document.documentElement.setAttribute('lang', code)
      if (this.$i18n) this.$i18n.locale = code
      if (window.pfCookieConsent && typeof window.pfCookieConsent.setLang === 'function') {
        window.pfCookieConsent.setLang(code)
      }
      this.langMenuOpen = false
    },
    downloadOnePager() {
      const blob = new Blob([`${this.$t('cta.onepager_title')}\n${this.$t('cta.onepager_body')}`], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'pinefitness-onepager.txt'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    },
    nextBooking(){
      if (!Array.isArray(this.bookingScreens) || this.bookingScreens.length < 2) return
      this.bookingIndex = (this.bookingIndex + 1) % this.bookingScreens.length
    },
    prevBooking(){
      if (!Array.isArray(this.bookingScreens) || this.bookingScreens.length < 2) return
      this.bookingIndex = (this.bookingIndex - 1 + this.bookingScreens.length) % this.bookingScreens.length
    },
    goBooking(i){
      if (typeof i !== 'number') return
      const len = this.bookingScreens.length
      if (len === 0) return
      this.bookingIndex = ((i % len) + len) % len
    },
    startBookingAutoplay(){
      if (this._bookingTimer) return
      this._bookingTimer = setInterval(() => { this.nextBooking() }, 4000)
    },
    stopBookingAutoplay(){
      if (this._bookingTimer) {
        clearInterval(this._bookingTimer)
        this._bookingTimer = null
      }
      // pauseOnly is kept for future use
    },
    nextShowcase(){
      if (this.isShowcaseInteracting) return
      if (!Array.isArray(this.showcaseScreens) || this.showcaseScreens.length < 2) return
      this.centerShowcase((this.showcaseIndex + 1) % this.showcaseScreens.length)
    },
    prevShowcase(){
      if (!Array.isArray(this.showcaseScreens) || this.showcaseScreens.length < 2) return
      this.centerShowcase((this.showcaseIndex - 1 + this.showcaseScreens.length) % this.showcaseScreens.length)
    },
    goShowcase(i){
      if (typeof i !== 'number') return
      const len = this.showcaseScreens.length
      if (len === 0) return
      this.centerShowcase(((i % len) + len) % len)
    },
    centerShowcase(i){
      const strip = this.$refs.showcaseStrip
      if (!strip) return
      const track = strip.querySelector('.showcase-track')
      const items = track ? Array.from(track.children) : []
      const item = items[i]
      if (!item) return
      const itemCenter = item.offsetLeft + item.offsetWidth / 2
      const target = itemCenter - strip.clientWidth / 2
      if (typeof strip.scrollTo === 'function') {
        strip.scrollTo({ left: target, behavior: 'smooth' })
      }
      this.showcaseIndex = i
    },
    startShowcaseAutoplay(){
      if (this._showcaseTimer || this.showcaseFocused || this.isShowcaseInteracting) return
      this._showcaseTimer = setInterval(() => {
        if (this.showcaseFocused || this.isShowcaseInteracting) return
        this.nextShowcase()
      }, 5500)
    },
    stopShowcaseAutoplay(){
      if (this._showcaseTimer) {
        clearInterval(this._showcaseTimer)
        this._showcaseTimer = null
      }
    }
  }
}
</script>

<style scoped>
/* Theme tokens */
:root { --pf-primary: #0ea5a4; --pf-primary-2: #10b981; --pf-surface: #ffffff; --pf-subtle: #f8fafc; }
.pro { background: var(--pf-subtle); }

.price-output {
  min-height: 1.75rem;
}

.price-loader {
  display: inline-flex;
  align-items: center;
  gap: 0.25em;
  letter-spacing: 0.12em;
  font-variant-numeric: tabular-nums;
}

.price-loader__currency {
  display: inline-block;
}

.price-loader__digits {
  display: inline-flex;
  justify-content: center;
  width: 3ch;
  animation: loader-flicker 0.6s ease-in-out infinite alternate;
}

@keyframes loader-flicker {
  from {
    opacity: 0.55;
    transform: translateY(-0.08em);
  }
  to {
    opacity: 1;
    transform: translateY(0.08em);
  }
}

/* Feature canvases */
.feature-canvas-grid {
  column-count: 1;
  column-gap: 18px;
}
@media (min-width: 768px) {
  .feature-canvas-grid { column-count: 2; }
}
@media (min-width: 1200px) {
  .feature-canvas-grid { column-count: 2; }
}
.feature-canvas-grid > * {
  break-inside: avoid;
}
.feature-canvas {
  display: inline-block;
  width: 100%;
  margin: 0 0 18px;
  position: relative;
  padding: 18px;
  border-radius: 20px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  box-shadow: 0 16px 42px rgba(15, 23, 42, 0.10);
  overflow: hidden;
  color: #0f172a;
  backdrop-filter: blur(8px);
  isolation: isolate;
}
.feature-canvas::after {
  content: '';
  position: absolute;
  inset: 8px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  opacity: 0.55;
  pointer-events: none;
}
.feature-canvas__header {
  font-size: 0.9rem;
  letter-spacing: .02em;
}
.feature-canvas__index {
  font-variant-numeric: tabular-nums;
  color: rgba(15, 23, 42, 0.6);
  font-weight: 700;
}
.feature-canvas__title {
  font-size: 1.15rem;
  margin: 8px 0 6px;
}
.feature-canvas__copy {
  margin-bottom: 12px;
}
.feature-canvas__body {
  display: flex;
  gap: 12px;
  align-items: stretch;
}
.feature-canvas__body--no-media {
  flex-direction: column;
}
.feature-canvas__list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.feature-canvas__list li {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 8px;
  align-items: start;
  font-size: 0.98rem;
}
.fc-bullet-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0ea5a4, #38bdf8);
  box-shadow: 0 0 0 6px rgba(14, 165, 164, 0.10);
  margin-top: 4px;
}
.feature-canvas__media {
  flex: 0 0 38%;
  max-width: 40%;
  border-radius: 16px;
  //background: rgba(255, 255, 255, 0.6);
  //border: 1px solid rgba(15, 23, 42, 0.08);
  //box-shadow: inset 0 1px 0 rgba(255,255,255,0.6), 0 8px 24px rgba(15,23,42,0.16);
  overflow: hidden;
}
.feature-canvas__media img {
  width: 100%;
  height: 100%;
  max-height: 230px;
  object-fit: cover;
  display: block;
}
.feature-canvas__video {
  display: grid;
  place-items: center;
  gap: 8px;
  height: 100%;
}
.feature-canvas__video-thumb {
  width: 100%;
  height: 160px;
  background: linear-gradient(135deg, #0f172a, #1e293b);
  background-size: cover;
  background-position: center;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
}
.feature-canvas__video-thumb::after {
  content: '▶';
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  color: #fff;
  font-size: 1.6rem;
  opacity: 0.82;
  text-shadow: 0 4px 18px rgba(0,0,0,0.35);
}
.feature-canvas__video-note {
  text-align: center;
  color: #0f172a;
}
.feature-canvas__media--placeholder {
  background: repeating-linear-gradient(
      135deg,
      rgba(15, 23, 42, 0.05),
      rgba(15, 23, 42, 0.05) 12px,
      rgba(15, 23, 42, 0.08) 12px,
      rgba(15, 23, 42, 0.08) 22px
    );
  border: 1px dashed rgba(15, 23, 42, 0.14);
  display: grid;
  place-items: center;
  padding: 14px;
}
.feature-canvas__video-thumb--ghost {
  background: linear-gradient(135deg, #e2e8f0, #cbd5e1);
  opacity: 0.45;
}
.fc-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 0.85rem;
  letter-spacing: .01em;
  border: 1px solid rgba(15, 23, 42, 0.12);
  background: rgba(255, 255, 255, 0.75);
  color: #0f172a;
  box-shadow: 0 6px 18px rgba(2, 6, 23, 0.06);
}
.fc-pill--app {
  background: linear-gradient(135deg, #ecfdf5, #d1fae5);
  border-color: rgba(16, 185, 129, 0.4);
}
.fc-pill--platform {
  background: linear-gradient(135deg, #e0f2fe, #e2e8f0);
  border-color: rgba(59, 130, 246, 0.4);
}
.fc-pill--media {
  background: linear-gradient(135deg, #fff7ed, #fef3c7);
  border-color: rgba(249, 115, 22, 0.4);
}
.feature-canvas--mint {
  background: linear-gradient(145deg, #ecfdf3 0%, #d1fae5 42%, #e0f2fe 100%);
  border-color: #befae3;
}
.feature-canvas--indigo {
  background: linear-gradient(160deg, #eef2ff 8%, #e0f2fe 52%, #ede9fe 100%);
  border-color: #d7defe;
}
.feature-canvas--sunrise {
  background: linear-gradient(150deg, #fff7ed 0%, #ffe4e6 48%, #fef3c7 100%);
  border-color: #ffe1c2;
}
.feature-canvas--sand {
  background: linear-gradient(160deg, #f8fafc 10%, #f1f5f9 58%, #fff7ed 100%);
  border-color: #e2e8f0;
}
.feature-canvas--slate {
  background: linear-gradient(160deg, #f8fafc 10%, #e2e8f0 56%, #cbd5e1 100%);
  border-color: #cbd5e1;
}
.feature-canvas--aqua {
  background: linear-gradient(150deg, #ecfeff 0%, #d9f99d 48%, #cffafe 100%);
  border-color: #bae6fd;
}
.feature-canvas--lavender {
  background: linear-gradient(150deg, #f5f3ff 6%, #e0e7ff 52%, #eef2ff 100%);
  border-color: #e0e7ff;
}
.feature-canvas--peach {
  background: linear-gradient(145deg, #fff1f2 0%, #ffe4e6 46%, #ffedd5 100%);
  border-color: #ffd7c2;
}
.feature-canvas--night {
  background: linear-gradient(150deg, #0f172a 0%, #1f2937 45%, #0b1223 100%);
  border-color: #1f2937;
  color: #e2e8f0;
}
.feature-canvas--night .feature-canvas__copy,
.feature-canvas--night .feature-canvas__list li span:last-child,
.feature-canvas--night .feature-canvas__index {
  color: #cbd5e1 !important;
}
.feature-canvas--night .fc-bullet-dot {
  box-shadow: 0 0 0 6px rgba(255, 255, 255, 0.08);
}
.feature-canvas--night .feature-canvas__video-note {
  color: #e2e8f0;
}
@media (max-width: 991px) {
  .feature-canvas__body {
    flex-direction: column;
  }
  .feature-canvas__media {
    max-width: 100%;
    flex: 1 1 auto;
  }
  .feature-canvas__media img {
    max-height: 200px;
  }
  .feature-canvas__media--placeholder {
    width: 100%;
  }
}


.showcase-focus-overlay {
  position: fixed;
  inset: 0;
  background: rgba(7, 12, 20, 0.78);
  backdrop-filter: blur(4px);
  z-index: 998;
  cursor: zoom-out;
}

.showcase-viewport--focused {
  position: relative;
  z-index: 999;
}

.showcase-viewport--focused .showcase-strip {
  border-radius: 24px;
  box-shadow: 0 28px 68px rgba(15, 23, 42, 0.35);
}

.showcase-viewport--focused .shot-item {
  cursor: zoom-out;
}

.showcase-viewport--focused .shot-item.active .shot-img {
  transform: scale(1.02);
}

:global(body.showcase-focus-lock) {
  overflow: hidden;
}

/* --- What You Get Modern Section --- */
.what-you-get-modern {
  max-width: 1180px;
  margin-left: auto;
  margin-right: auto;
  position: relative;
  z-index: 2;
}
.wyget-features {
  background: rgba(255,255,255,0.55);
  border-radius: 24px;
  box-shadow: 0 12px 38px rgba(16,185,129,0.06), 0 1.5px 0 rgba(16,185,129,.03) inset;
  backdrop-filter: blur(13px) saturate(125%);
  border: 1px solid rgba(2,6,23,.06);
  overflow: hidden;
  --divider-color: rgba(14, 165, 164, 0.12);
}
.wyget-feature {
  min-width: 220px;
  max-width: 330px;
  flex:1 1 220px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
}
.wyget-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-size: 2rem;
  margin-bottom: 10px;
  box-shadow: 0 4px 28px 0 rgba(16,185,129,.13);
}
.gradient-emerald-glassy {
  background: linear-gradient(135deg, #ecfdf5 60%, #d1fae5 100%),linear-gradient(135deg, #10b981 50%, #0ea5a4 90%);
  border: 1.5px solid #10b98133;
}
.bg-gradient-indigo {
  background: linear-gradient(135deg, #eef2ff 65%, #6366f1 100%);
  border: 1.5px solid #6366f133;
}
.bg-gradient-pink {
  background: linear-gradient(135deg, #fce7f3 65%, #ec4899 98%);
  border: 1.5px solid #ec489933;
}
.bg-gradient-yellow {
  background: linear-gradient(135deg, #fff7ed 60%, #fbbf24 100%);
  border: 1.5px solid #f59e4233;
}
.bg-gradient-cyan {
  background: linear-gradient(135deg, #ecfeff 65%, #06b6d4 100%);
  border: 1.5px solid #06b6d444;
}
.elevation {
  box-shadow: 0 8px 28px rgba(2,6,23,.09), 0 1.5px 0 rgba(2,6,23,.02) inset;
}

.wyget-details {
  color: #475569 !important;
  opacity: .93;
}

/* Glow border around wyget-features on hover */
.wyget-features {
  position: relative;
  border-radius: 20px; /* soft rounding for glow path */
  isolation: isolate; /* keep glow effects contained */
  /* interactive glow position */
  --glowX: 50%;
  --glowY: 50%;
}

/*.wyget-features:hover::after { opacity: .9; } */
.wyget-divider {
  width: 1px;
  background: var(--divider-color);
  margin: 0 0px;
  flex-shrink: 0;
  border-radius: 99px;
  min-height: 48px;
  align-self: center;
  box-shadow: 0 0 0 0 rgba(2,6,23,0.0);
}

/* Responsive: stack on mobile */
@media (max-width: 991px) {
  .wyget-features {
    flex-direction: column !important;
    padding: 0.5rem 0.3rem;
  }
  .wyget-divider { display: none !important; }
  .wyget-feature { max-width: none; min-width: 180px; }
}

a.nav-link {
  color: #343946;
  font-weight: 600;
}

.logo-name{
  font-family: sans-serif;
  font-weight: 600;
  margin-bottom: 0;
}

/* Smooth shadows/borders */
.shadow-soft { box-shadow: 0 10px 30px rgba(2, 6, 23, .06), 0 1px 0 rgba(2,6,23,.06) inset; }
/*.card-pro { border: 1px solid rgba(2,6,23,.08); border-radius: 1rem; background: var(--pf-surface); box-shadow: 0 6px 24px rgba(2,6,23,.05); transition: transform .25s ease, box-shadow .25s ease; }
.card-pro:hover { transform: translateY(-2px); box-shadow: 0 14px 34px rgba(2,6,23,.08); }
.card-pro--featured { outline: 2px solid color-mix(in srgb, var(--pf-primary) 30%, white); }
*/

/* Glass card – smoother hover */
.card-pro {
  position: relative;
  background-color: rgba(255,255,255,0.17);
  border-radius: 24px;
  border: 1.5px solid rgba(255,255,255,0.35);
  backdrop-filter: blur(18px) saturate(160%);
  -webkit-backdrop-filter: blur(18px) saturate(160%);

  box-shadow: 0 8px 40px rgba(48,104,197,0.16);
  overflow: hidden;

  /* Interactive glow (light) */
  --glowX: 50%;
  --glowY: 50%;
  --glowOpacity: 0; /* 0..1 */
  background-image:
      radial-gradient(200px 140px at var(--glowX) var(--glowY),
        rgba(99,102,241, calc(var(--glowOpacity) * 0.08)),
        rgba(6,182,212, calc(var(--glowOpacity) * 0.04)) 70%,
        rgba(16,185,129, 0) 100%
      );
  background-blend-mode: screen; /* keeps it light and glassy */

  /* Only animate cheap properties */
  transition:
      box-shadow 280ms cubic-bezier(.19,.83,.29,1),
      transform 260ms cubic-bezier(.19,.83,.29,1),
      background-color 260ms cubic-bezier(.19,.83,.29,1),
      border-color 260ms cubic-bezier(.19,.83,.29,1),
      background-image 160ms ease;

  /* Compositing hints */
  will-change: transform, box-shadow;
  transform: translateZ(0);
  backface-visibility: hidden;
  contain: paint;
}

.card-pro:hover {
  /* Small, crisp lift instead of huge shrink */
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 16px 48px rgba(48,104,197,0.20);
  background-color: rgba(255,255,255,0.22);
  border-color: rgba(181,215,255,0.45);
  --glowOpacity: 1;
}

#pricing {
  position: relative;
  isolation: isolate;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: var(--pricing-bg-image, none);
    background-size: 135%;
    background-position: center;
    background-repeat: no-repeat;
    z-index: 0;
  }

  @media (max-width: 792px){
    &::before {
      background-size: 235%;
    }
  }

  > * {
    position: relative;
    z-index: 1;
  }

  /* Animated shine – PAUSED by default */

  .card-pro::before,
  .card-pro::after {
    animation-play-state: paused;
  }

  /* Only animate while hovered/focused (better perf on lists) */

  .card-pro:hover::before,
  .card-pro:hover::after,
  .card-pro:focus-visible::before,
  .card-pro:focus-visible::after {
    animation-play-state: running;
  }

  /* Shine sweep */

  .card-pro::before {
    content: '';
    pointer-events: none;
    position: absolute;
    top: -80%;
    left: -60%;
    width: 280%;
    height: 200%;
    background: linear-gradient(
        120deg,
        rgba(255, 255, 255, 0) 45%,
        rgba(255, 255, 255, 0.45) 50%,
        rgba(255, 255, 255, 0.05) 58%,
        rgba(255, 255, 255, 0) 62%
    );
    transform: skewX(-20deg);
    opacity: 0.47;
    z-index: 2;
    animation: glass-shine 3.4s linear infinite;
  }

  /* Light blob */

  .card-pro::after {
    content: '';
    pointer-events: none;
    position: absolute;
    right: 12%;
    bottom: 24%;
    width: 38px;
    height: 38px;
    background: radial-gradient(
        ellipse 64% 92% at 52% 48%,
        rgba(255, 255, 255, 0.90) 0%,
        rgba(173, 212, 255, 0.1) 72%,
        rgba(255, 255, 255, 0) 100%
    );
    opacity: 0.28;
    z-index: 3;
    filter: blur(2.2px);
    animation: glass-blob 3.1s ease-in-out infinite alternate-reverse;
  }
}
  /* Keep your keyframes as-is */
  @keyframes glass-shine {
    0% {
      left: -60%;
    }
    100% {
      left: 100%;
    }
  }
  @keyframes glass-blob {
    0% {
      transform: scale(1) translateY(0);
      opacity: 0.28;
    }
    55% {
      opacity: 0.44;
    }
    80% {
      transform: scale(1.18) translateY(-7px);
    }
    100% {
      transform: scale(1.16) translateY(-8px);
      opacity: 0.22;
    }
  }

  /* Motion prefs: disable the animations entirely */
  @media (prefers-reduced-motion: reduce) {
    .card-pro,
    .card-pro::before,
    .card-pro::after {
      transition: none !important;
      animation: none !important;
    }
  }

.surface { border: 1px solid rgba(2,6,23,.08); background: var(--pf-surface); border-radius: 1rem; box-shadow: 0 6px 24px rgba(2,6,23,.04); }
.surface--subtle { border: 1px solid rgba(2,6,23,.06); background: linear-gradient(180deg, #fff, #f9fbfd); }
.card-quote { border-radius: 1rem; border: 1px dashed rgba(2,6,23,.15); background: linear-gradient(180deg, #fff, #fafcff); }
/*.icon-badge { display: inline-grid; place-items: center; width: 40px; height: 40px; border-radius: 12px; background: linear-gradient(180deg, #f5fbff, #eef7ff); border: 1px solid rgba(2,6,23,.08); }
*/
/* Add or replace inside <style scoped> */
.icon-badge {
  width: 45px;
  height: 45px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-size: 2rem;
  margin-bottom: 10px;
  /* Copy the indigo badge style */
  background: linear-gradient(135deg, #eef2ff 65%, #3f8ba3 100%);
  border: 1.5px solid #6366f133;
  box-shadow: 0 8px 28px rgba(2,6,23,.09), 0 1.5px 0 rgba(2,6,23,.02) inset;
}

/* Nav */
.bg-blur { backdrop-filter: saturate(120%) blur(6px); background: rgba(255,255,255,.85); transition: box-shadow .2s ease; }
header.elev { box-shadow: 0 6px 24px rgba(2,6,23,.06); }
.brand-mark { width: 32px; height: 32px; border-radius: 10px; background: conic-gradient(from 180deg, var(--pf-primary), var(--pf-primary-2)); box-shadow: inset 0 1px 2px rgba(255,255,255,.6); }

/* Hero */
.hero-overlay { position:absolute; inset:0; z-index:1; background: linear-gradient(180deg, rgba(15,23,42,.65), rgba(15,23,42,.65)); }
#hero {
  min-height: calc(100vh - var(--header-h, 0px));
  min-height: calc(100svh - var(--header-h, 0px));
  display: flex;
  align-items: center;
  isolation: isolate;
  color: #0b1f30;
  background:
    radial-gradient(circle at 76% 12%, rgba(88, 224, 222, .2), transparent 28%),
    linear-gradient(135deg, #f9fffe 0%, #f1fbfa 48%, #eef8fb 100%);
}
.hero-container {
  z-index: 2;
  padding-top: clamp(4.5rem, 8vh, 7rem);
  padding-bottom: clamp(4.5rem, 8vh, 7rem);
}
.hero-grid {
  position: absolute;
  inset: 0;
  z-index: -3;
  opacity: .35;
  background-image:
    linear-gradient(rgba(18, 112, 119, .07) 1px, transparent 1px),
    linear-gradient(90deg, rgba(18, 112, 119, .07) 1px, transparent 1px);
  background-size: 52px 52px;
  mask-image: linear-gradient(to bottom, rgba(0,0,0,.7), transparent 88%);
  -webkit-mask-image: linear-gradient(to bottom, rgba(0,0,0,.7), transparent 88%);
}
.hero-aurora {
  position: absolute;
  z-index: -2;
  border-radius: 999px;
  filter: blur(4px);
  pointer-events: none;
}
.hero-aurora--one {
  width: 430px;
  height: 430px;
  right: -120px;
  top: 6%;
  background: rgba(33, 204, 190, .16);
}
.hero-aurora--two {
  width: 330px;
  height: 330px;
  left: -180px;
  bottom: -100px;
  background: rgba(64, 178, 224, .12);
}
.hero-kicker {
  display: inline-flex;
  align-items: center;
  gap: .62rem;
  margin-bottom: 1.35rem;
  color: #087b7a;
  font-size: .78rem;
  font-weight: 800;
  letter-spacing: .1em;
  text-transform: uppercase;
}
.hero-kicker__mark {
  width: 30px;
  height: 2px;
  border-radius: 99px;
  background: linear-gradient(90deg, #0ea5a4, #3bc8ef);
  box-shadow: 0 0 14px rgba(14, 165, 164, .45);
}
.hero-title {
  max-width: 760px;
  margin: 0;
  color: #102437;
  font-size: clamp(3rem, 5.4vw, 5.15rem);
  font-weight: 850;
  letter-spacing: -.055em;
  line-height: .99;
  text-wrap: balance;
}
.hero-title span {
  display: block;
  padding-bottom: .07em;
  color: transparent;
  background: linear-gradient(105deg, #098b88 8%, #12a8a4 50%, #1a94bc 95%);
  background-clip: text;
  -webkit-background-clip: text;
}
.hero-subtitle {
  max-width: 650px;
  margin: 1.55rem 0 0;
  color: #536879;
  font-size: clamp(1.05rem, 1.45vw, 1.22rem);
  line-height: 1.65;
}
.hero-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.25rem;
  margin-top: 2rem;
}
.hero-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: .7rem;
  min-height: 56px;
  padding: .8rem 1.45rem;
  color: #fff;
  border: 1px solid #087e7e;
  border-radius: 14px;
  background: linear-gradient(135deg, #0faaa6, #087e91);
  box-shadow: 0 14px 32px rgba(8, 126, 145, .23), inset 0 1px rgba(255,255,255,.24);
  font-weight: 750;
  transition: transform .2s ease, box-shadow .2s ease;
}
.hero-primary:hover,
.hero-primary:focus {
  color: #fff;
  transform: translateY(-2px);
  box-shadow: 0 18px 38px rgba(8, 126, 145, .3), inset 0 1px rgba(255,255,255,.24);
}
.hero-primary > span { transition: transform .2s ease; }
.hero-primary:hover > span { transform: translateX(3px); }
.hero-secondary {
  display: inline-flex;
  align-items: center;
  gap: .7rem;
  color: #173a4b;
  font-weight: 750;
  text-decoration: none;
}
.hero-secondary:hover { color: #087b7a; }
.hero-secondary__play {
  display: grid;
  width: 38px;
  height: 38px;
  place-items: center;
  color: #0a8e8d;
  border: 1px solid rgba(10, 142, 141, .26);
  border-radius: 999px;
  background: rgba(255,255,255,.72);
  box-shadow: 0 8px 22px rgba(18, 59, 74, .08);
}
.hero-demo-note {
  margin: .75rem 0 0;
  color: #738592;
  font-size: .82rem;
}
.hero-proof-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: .9rem;
  margin-top: 2.1rem;
  padding-top: 1.55rem;
  border-top: 1px solid rgba(33, 86, 101, .13);
}
.hero-proof {
  display: flex;
  align-items: flex-start;
  gap: .65rem;
  min-width: 0;
  color: #6d808d;
  font-size: .74rem;
  line-height: 1.35;
}
.hero-proof strong {
  display: block;
  margin-bottom: .18rem;
  color: #173747;
  font-size: .82rem;
}
.hero-proof__icon {
  display: grid;
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  place-items: center;
  color: #0a908d;
  border: 1px solid rgba(10, 144, 141, .16);
  border-radius: 10px;
  background: rgba(255,255,255,.66);
}
.hero-product {
  position: relative;
  z-index: 1;
  max-width: 720px;
  margin: 1rem auto 0;
  padding: 2.7rem 0 4.3rem;
}
.hero-product__glow {
  position: absolute;
  z-index: -1;
  inset: 8% 3% 14%;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(13, 164, 161, .28), rgba(55, 177, 220, .2));
  filter: blur(42px);
}
.hero-live-dot { width: 7px; height: 7px; border-radius: 50%; background: #44d89b; box-shadow: 0 0 0 4px rgba(68,216,155,.12); }
.hero-ecosystem {
  min-height: 535px;
  padding: 3.2rem 0 3rem;
}
.hero-connection {
  position: absolute;
  z-index: -1;
  border: 1px dashed rgba(14, 165, 164, .32);
  border-radius: 50%;
}
.hero-connection--one { width: 390px; height: 390px; right: 0; top: 3.4rem; }
.hero-connection--two { width: 250px; height: 250px; right: 4.4rem; top: 7.6rem; }
.hero-ops-panel {
  position: relative;
  width: 84%;
  overflow: hidden;
  padding: 1.15rem;
  color: #eaf9fa;
  border: 1px solid rgba(255,255,255,.12);
  border-radius: 24px;
  background:
    radial-gradient(circle at 0 0, rgba(36, 192, 188, .16), transparent 38%),
    linear-gradient(145deg, #102b35, #0d202a 70%);
  box-shadow: 0 34px 75px rgba(20, 58, 70, .24), inset 0 1px rgba(255,255,255,.08);
  transform: perspective(1400px) rotateY(3deg) rotateX(1deg);
}
.hero-ops-panel::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(115deg, transparent 25%, rgba(255,255,255,.04) 48%, transparent 70%);
}
.hero-ops-panel__head,
.hero-ops-panel__identity,
.hero-ops-panel__live,
.hero-ops-schedule__title,
.hero-class-row { display: flex; align-items: center; }
.hero-ops-panel__head { justify-content: space-between; gap: 1rem; padding-bottom: .95rem; }
.hero-ops-panel__identity { gap: .65rem; min-width: 0; }
.hero-ops-panel__identity > span:last-child { min-width: 0; }
.hero-ops-panel__identity small { display: block; color: #8aa7b1; font-size: .59rem; line-height: 1.2; }
.hero-ops-panel__identity strong { display: block; margin-top: .12rem; overflow: hidden; font-size: .84rem; text-overflow: ellipsis; white-space: nowrap; }
.hero-ops-panel__logo { display: grid; width: 36px; height: 36px; flex: 0 0 36px; place-items: center; color: #69e1dc; border: 1px solid rgba(105,225,220,.2); border-radius: 11px; background: rgba(105,225,220,.08); }
.hero-ops-panel__live { gap: .45rem; padding: .38rem .58rem; color: #92e5c1; border: 1px solid rgba(89,218,157,.16); border-radius: 999px; background: rgba(89,218,157,.07); font-size: .6rem; font-weight: 800; text-transform: uppercase; }
.hero-ops-stats { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: .52rem; }
.hero-ops-stat { min-width: 0; padding: .72rem .7rem; border: 1px solid rgba(255,255,255,.07); border-radius: 13px; background: rgba(255,255,255,.045); }
.hero-ops-stat > span { display: block; overflow: hidden; color: #8eabb5; font-size: .57rem; text-overflow: ellipsis; white-space: nowrap; }
.hero-ops-stat strong { display: inline-block; margin-top: .3rem; color: #fff; font-size: 1.13rem; font-variant-numeric: tabular-nums; }
.hero-ops-stat small { margin-left: .3rem; color: #52d5a0; font-size: .55rem; }
.hero-ops-schedule { margin-top: .7rem; padding: .8rem; border: 1px solid rgba(255,255,255,.07); border-radius: 15px; background: rgba(3, 13, 19, .22); }
.hero-ops-schedule__title { justify-content: space-between; padding: 0 .1rem .6rem; }
.hero-ops-schedule__title strong { font-size: .72rem; }
.hero-ops-schedule__title span { color: #64d6d3; font-size: .55rem; font-weight: 700; }
.hero-class-row { gap: .65rem; min-height: 49px; padding: .48rem .2rem; border-top: 1px solid rgba(255,255,255,.06); }
.hero-class-row time { width: 34px; flex: 0 0 34px; color: #86a4ae; font-size: .59rem; font-variant-numeric: tabular-nums; }
.hero-class-row > span:nth-child(2) { min-width: 0; flex: 1; }
.hero-class-row strong { display: block; overflow: hidden; font-size: .66rem; text-overflow: ellipsis; white-space: nowrap; }
.hero-class-row small { display: block; margin-top: .12rem; color: #78949e; font-size: .54rem; }
.hero-class-row__capacity { padding: .28rem .42rem; color: #84dedb; border-radius: 7px; background: rgba(41,192,187,.1); font-size: .55rem; font-weight: 800; font-variant-numeric: tabular-nums; }
.hero-class-row__capacity.is-full { color: #f8cb78; background: rgba(248,203,120,.1); }
.hero-member-phone {
  position: absolute;
  z-index: 3;
  top: 5.4rem;
  right: -.2rem;
  width: 215px;
  min-height: 390px;
  overflow: hidden;
  padding: 1.2rem .82rem .75rem;
  color: #183541;
  border: 7px solid #153039;
  border-radius: 31px;
  background: linear-gradient(180deg, #fbffff, #f1f9f8);
  box-shadow: 0 30px 64px rgba(12, 47, 58, .28), inset 0 0 0 1px rgba(255,255,255,.7);
  transform: rotate(2deg);
}
.hero-member-phone__notch { position: absolute; top: 0; left: 50%; width: 72px; height: 17px; border-radius: 0 0 13px 13px; background: #153039; transform: translateX(-50%); }
.hero-member-phone__top,
.hero-member-phone__brand,
.hero-member-upcoming__title,
.hero-member-phone__nav { display: flex; align-items: center; }
.hero-member-phone__top { justify-content: space-between; margin-top: .25rem; }
.hero-member-phone__brand { gap: .38rem; min-width: 0; }
.hero-member-phone__brand img { border-radius: 7px; }
.hero-member-phone__brand strong { overflow: hidden; font-size: .66rem; text-overflow: ellipsis; white-space: nowrap; }
.hero-member-phone__avatar { display: grid; width: 24px; height: 24px; place-items: center; color: #0d8583; border-radius: 50%; background: #dff4f1; font-size: .58rem; font-weight: 800; }
.hero-member-phone > p { margin: 1.05rem 0 .7rem; font-size: .73rem; font-weight: 800; }
.hero-member-glance { display: grid; grid-template-columns: 1fr 1fr; gap: .48rem; }
.hero-member-glance > div { padding: .65rem .58rem; border: 1px solid rgba(15, 117, 118, .08); border-radius: 12px; background: #fff; box-shadow: 0 7px 18px rgba(25,70,78,.06); }
.hero-member-glance small { display: block; min-height: 20px; color: #7a9098; font-size: .49rem; line-height: 1.25; }
.hero-member-glance strong { display: block; margin-top: .16rem; color: #0a8e8a; font-size: 1.15rem; }
.hero-member-upcoming { margin-top: .65rem; padding: .78rem; color: #eafcfa; border-radius: 15px; background: linear-gradient(145deg, #0e918c, #126b7d); box-shadow: 0 12px 25px rgba(13,126,131,.2); }
.hero-member-upcoming > span { font-size: .52rem; font-weight: 800; letter-spacing: .07em; text-transform: uppercase; opacity: .78; }
.hero-member-upcoming__title { justify-content: space-between; margin-top: .55rem; }
.hero-member-upcoming__title strong { font-size: .76rem; }
.hero-member-upcoming__title span { display: grid; width: 20px; height: 20px; place-items: center; color: #0c766f; border-radius: 50%; background: #a9f1d1; font-size: .58rem; }
.hero-member-upcoming small { display: block; margin-top: .42rem; color: rgba(255,255,255,.74); font-size: .51rem; }
.hero-member-phone__nav { position: absolute; right: .8rem; bottom: .65rem; left: .8rem; justify-content: space-around; padding-top: .6rem; color: #8ca0a6; border-top: 1px solid rgba(17,80,90,.08); }
.hero-member-phone__nav span { display: grid; width: 27px; height: 27px; place-items: center; border-radius: 9px; }
.hero-member-phone__nav .is-active { color: #0d9390; background: #e0f5f2; }
.hero-event-card {
  position: absolute;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: .65rem;
  padding: .67rem .8rem;
  border: 1px solid rgba(255,255,255,.92);
  border-radius: 14px;
  background: rgba(255,255,255,.92);
  box-shadow: 0 15px 38px rgba(18,59,74,.16);
  backdrop-filter: blur(14px) saturate(140%);
  -webkit-backdrop-filter: blur(14px) saturate(140%);
}
.hero-event-card--payment { top: 1.05rem; right: .3rem; }
.hero-event-card--waitlist { bottom: .7rem; left: 2.2rem; }
.hero-event-card__icon { display: grid; width: 35px; height: 35px; place-items: center; border-radius: 11px; }
.hero-event-card__icon.is-blue { color: #138ba5; background: #e4f7fb; }
.hero-event-card__icon.is-green { color: #0c9170; background: #e3f8f0; }
.hero-event-card small { display: block; margin-bottom: .08rem; color: #7a8d96; font-size: .61rem; }
.hero-event-card strong { display: block; color: #163844; font-size: .77rem; white-space: nowrap; }
.hero-scale {
  margin-top: 1.65rem;
  padding: 1.1rem 1.2rem 1.2rem;
  border: 1px solid rgba(19, 121, 124, .14);
  border-radius: 18px;
  background: rgba(255, 255, 255, .7);
  box-shadow: 0 14px 36px rgba(18, 59, 74, .08);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}
.hero-scale__eyebrow {
  margin: 0 0 .75rem;
  color: #197d7d;
  font-size: .68rem;
  font-weight: 800;
  letter-spacing: .1em;
  text-transform: uppercase;
}
.hero-scale__metrics {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: .75rem;
}
.hero-scale__item { display: flex; flex: 1 1 0; flex-direction: column; gap: .22rem; min-width: 0; }
.hero-scale__item strong { color: #143849; font-size: clamp(1.55rem, 2.2vw, 1.9rem); line-height: 1; font-variant-numeric: tabular-nums; }
.hero-scale__item span { color: #657985; font-size: .7rem; font-weight: 650; line-height: 1.25; }
.hero-scale__divider { flex: 0 0 1px; width: 1px; height: 42px; background: rgba(25, 75, 88, .16); }
@media (max-width: 1199px) {
  .hero-title { font-size: clamp(2.85rem, 5vw, 4.4rem); }
  .hero-proof-list { gap: .6rem; }
  .hero-proof { gap: .45rem; }
}
@media (max-width: 991px) {
  #hero { min-height: auto; }
  .hero-container { padding-top: 4.8rem; }
  .hero-title, .hero-subtitle { max-width: 760px; }
  .hero-product { margin-top: 1.4rem; }
  .hero-ecosystem { max-width: 680px; min-height: 560px; }
  .hero-ops-panel { transform: none; }
}
@media (max-width: 767px) {
  .hero-container { padding-top: 3.7rem; padding-bottom: 4rem; }
  .hero-kicker { font-size: .68rem; letter-spacing: .075em; }
  .hero-title { font-size: clamp(2.55rem, 12.2vw, 3.65rem); }
  .hero-subtitle { font-size: 1rem; line-height: 1.58; }
  .hero-actions { align-items: stretch; flex-direction: column; gap: 1rem; }
  .hero-primary { width: 100%; }
  .hero-secondary { justify-content: center; }
  .hero-demo-note { text-align: center; }
  .hero-proof-list { grid-template-columns: 1fr; }
  .hero-proof { align-items: center; }
  .hero-product { padding: 2.6rem 0 6.8rem; }
  .hero-ecosystem { min-height: 535px; padding-bottom: 3rem; }
  .hero-ops-panel { width: 88%; }
  .hero-member-phone { right: 0; width: 200px; }
  .hero-event-card--payment { right: .2rem; }
  .hero-event-card--waitlist { left: .4rem; }
  .hero-scale { margin-top: 1.5rem; padding: 1rem .8rem; }
  .hero-scale__eyebrow { margin-bottom: .7rem; text-align: center; }
  .hero-scale__metrics { gap: .45rem; }
  .hero-scale__item { align-items: center; text-align: center; }
  .hero-scale__item strong { font-size: 1.45rem; }
  .hero-scale__divider { height: 38px; }
}
@media (max-width: 420px) {
  .hero-scale { padding-right: .45rem; padding-left: .45rem; }
  .hero-scale__metrics { gap: .3rem; }
  .hero-scale__item span { font-size: .59rem; }
  .hero-ecosystem { min-height: 500px; }
  .hero-ops-panel { width: 94%; padding: .85rem; }
  .hero-ops-panel__identity small { display: none; }
  .hero-ops-panel__identity strong { max-width: 130px; }
  .hero-ops-stats { gap: .32rem; }
  .hero-ops-stat { padding: .58rem .48rem; }
  .hero-ops-stat strong { font-size: .95rem; }
  .hero-ops-stat small { display: none; }
  .hero-ops-schedule { padding: .62rem; }
  .hero-member-phone { top: 5.8rem; right: -.35rem; width: 174px; min-height: 355px; padding-right: .65rem; padding-left: .65rem; }
  .hero-member-phone__brand strong { max-width: 74px; }
  .hero-member-glance small { min-height: 25px; }
  .hero-event-card--payment { top: .95rem; }
  .hero-event-card--waitlist { bottom: .35rem; left: .15rem; }
  .hero-class-row { gap: .4rem; }
  .hero-class-row__capacity { display: none; }
}
@media (prefers-reduced-motion: reduce) {
  .hero-primary, .hero-primary > span { transition: none; }
}

/* Section background (Features) */
#features { min-height: 100%; }
#features .section-bg { position:absolute; inset:0; background-image:var(--bg); background-size:cover; background-position:center; background-attachment:fixed; z-index:0; }
#features .section-overlay { position:absolute; inset:0; z-index:1; background: linear-gradient(180deg, rgba(2,6,23,.55), rgba(2,6,23,.45)); }
#features .container { position:relative; z-index:2; }

/* How it works */
.how-card {
  border: 1px solid rgba(15,23,42,.06);
  box-shadow: 0 18px 50px rgba(15,23,42,.08);
  border-radius: 24px;
  background: linear-gradient(135deg, #f8fafc, #e0f2fe);
}
.how-step {
  background: rgba(255,255,255,0.78);
  border: 1px solid rgba(2,6,23,.06);
  border-radius: 16px;
  padding: 16px 18px 18px;
  box-shadow: 0 12px 34px rgba(2,6,23,.08);
  position: relative;
  overflow: hidden;
}
.how-step:before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 20% 15%, rgba(14,165,233,.12), transparent 36%);
  pointer-events: none;
}
.how-step__badge {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  font-weight: 800;
  color: #0f172a;
  background: linear-gradient(135deg, #a7f3d0, #22c55e);
  border: 1px solid rgba(34,197,94,.3);
  box-shadow: 0 10px 26px rgba(34,197,94,.28);
  margin-bottom: 14px;
}
.how-step h3 { color: #0f172a; }

/* CTA */
.gradient-emerald { background: linear-gradient(135deg, var(--pf-primary-2), var(--pf-primary)); color:#fff; border-radius:1rem; padding:2rem; box-shadow:0 18px 50px rgba(16,185,129,.22); }

/* Bookings glass section */
.bookings-glass {
  padding:40px;
  border-radius: 20px;
  background: rgba(255,255,255,0.18);
  border: 1px solid rgba(255,255,255,0.38);
  backdrop-filter: blur(14px) saturate(150%);
  -webkit-backdrop-filter: blur(14px) saturate(150%);
  position: relative;
  /* Allow visuals to overflow the glass card */
  overflow: visible;
}
.bookings-visual { position: relative; width: 100%; text-align: center; }
.bookings-visual picture { display: block; width: 100%; height: 100%; }
.bookings-visual .shot { width: 100%; height: 520px; border-radius: 18px; overflow: hidden;
/*  background: #0b1223;
  border: 1px solid rgba(2,6,23,.08);
  box-shadow: 0 14px 40px rgba(2,6,23,.12);  */
  display: inline-flex;
}
.bookings-visual .shot-img {
  width: 100%;
  border-radius: 20px;
  -o-object-fit: contain;
  object-fit: contain;
  z-index: -1;
  display: block;
}
@media (max-width: 991px) {
  .bookings-visual .shot { width: 100%; }
}
.bookings-glass::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: radial-gradient(60% 60% at 0% 0%, rgba(16,185,129,.16), transparent 60%),
              radial-gradient(40% 40% at 100% 0%, rgba(99,102,241,.12), transparent 60%);
}
.bookings-list .icon-badge-sm {
  min-width: 34px;
  width: 34px; height: 34px; border-radius: 10px; display:grid; place-items:center;
  background: linear-gradient(135deg, #eef2ff 65%, #ffffff 100%);
  border: 1px solid rgba(2,6,23,.08);
  box-shadow: 0 8px 28px rgba(2,6,23,.06), 0 1.5px 0 rgba(2,6,23,.02) inset;
}

/* Device stack (legacy in hero) */
.device-stack { position: relative; display:flex; align-items:center; justify-content:center; gap: 0; }
.device-stack .device-frame { border-radius: 28px; background: rgba(255,255,255,0.22); padding: 10px; border: 1px solid rgba(2,6,23,.08); position: relative; }
.device-stack .device-screen { border-radius: 22px; overflow: hidden; background: #0b1223; display: grid; place-items: center; }

/* Carousel controls */
.carousel-btn { position: absolute; top: 50%; transform: translateY(-50%); width: 36px; height: 36px; border-radius: 999px; border: 1px solid rgba(255,255,255,.6); background: rgba(255,255,255,.8); color:#0b1220; display:grid; place-items:center; box-shadow: 0 10px 24px rgba(2,6,23,.12); z-index: 5; }
.carousel-btn.prev { left: 12px; }
.carousel-btn.next { right: 12px; }
.carousel-btn:hover { background: #fff; }
.dots { position: absolute; bottom: -18px; left: 50%; transform: translateX(-50%); display:flex; gap:8px; }
.dot { width:8px; height:8px; border-radius:999px; background: rgba(2,6,23,.2); border: 1px solid rgba(2,6,23,.2); }
.dot.active { background: rgb(58 204 244); border-color: rgb(58 204 244); }

/* Fade transition */
.fade-enter-active, .fade-leave-active { transition: opacity .4s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

/* Buttons micro‑interactions */
.btn-glow { position: relative; overflow: hidden; }
.btn-glow::after { content: ""; position: absolute; inset: 0; background: radial-gradient(650px circle at var(--x,50%) var(--y,50%), rgba(255,255,255,.35), transparent 40%); opacity: 0; transition: opacity .35s; }
.btn-glow:hover::after { opacity: 1; }
.btn-press { transition: transform .1s ease; }
.btn-press:active { transform: translateY(1px); }

/* Reveal on scroll */
.reveal { transition: transform .5s cubic-bezier(.22,.61,.36,1), opacity .5s ease; }
html.js .reveal { opacity: 0; transform: translateY(12px); }
.reveal.d1 { transition-delay: .08s }
.reveal.d2 { transition-delay: .16s }
.reveal.d3 { transition-delay: .24s }
.reveal-in { opacity: 1 !important; transform: translateY(0) !important; }

/* Custom slide-in from right for bookings visual */
html.js .slide-in-right.reveal { opacity: 0; transform: translateX(48px); }
.slide-in-right.reveal-in { opacity: 1 !important; transform: translateX(0) !important; }

/* Accessibility / fallbacks */
@media (prefers-reduced-motion: reduce) { .reveal, .btn-glow::after, .card-pro { transition: none !important; } }
@supports (-webkit-touch-callout: none) { #features .section-bg { background-attachment: scroll; } }

/* Language switcher */
.lang-switcher .lang-btn { font-weight: 600; color: #343946; }
.lang-flag {
  width: 20px;
  height: 14px;
  display: block;
  flex-shrink: 0;
  border-radius: 2px;
  object-fit: cover;
  box-shadow: 0 0 0 1px rgba(15,23,42,.08);
}
.lang-code { letter-spacing: .02em; }
.lang-switcher .chev { opacity: .7; }
.lang-menu {
  position: absolute;
  right: 0;
  top: calc(100% + 6px);
  z-index: 50;
  background: #fff;
  border-radius: 12px;
  border: 1px solid rgba(2,6,23,.08);
  min-width: 180px;
  padding: 6px;
}
.lang-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  border: 0;
  background: transparent;
  text-align: left;
  border-radius: 8px;
  color: #0f172a;
}
.lang-item:hover { background: #f1f5f9; }
.lang-item[aria-selected="true"] { background: #eef2ff; }
.lang-name { font-size: .9rem; }

/* Partner logos marquee */
.logo-scroller {
  overflow: hidden;
  position: relative;
  padding: 8px 0;
  mask-image: linear-gradient(to right, transparent, black 10%, black 90%, transparent);
  -webkit-mask-image: linear-gradient(to right, transparent, black 10%, black 90%, transparent);
}
.logo-track {
  display: flex;
  align-items: center;
  gap: 36px;
  width: max-content;
  margin: 0 auto;
  /* animation disabled by default; enabled when .logo-scroller--animate */
}
.logo-scroller--animate .logo-track { animation: logo-scroll 28s linear infinite; }

/* Studio Types chips */
.studio-chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  margin-top: 12px;
  padding-left: 0;
  list-style: none;
}
.studio-chip {
  padding: 8px 12px;
  border-radius: 999px;
  border: 1px solid rgba(2,6,23,.08);
  background: linear-gradient(135deg, #ffffff 60%, #f1f5f9 100%);
  color: #0f172a;
  font-size: .95rem;
  box-shadow: 0 8px 28px rgba(2,6,23,.06), 0 1.5px 0 rgba(2,6,23,.02) inset;
}

/* Studio Types masonry (unstructured) */
.studio-masonry .card.card-pro.h-100.text-dark {
  box-shadow: none;
}
.studio-masonry .card.card-pro.h-100.text-dark:hover {
  box-shadow: 0 8px 40px rgba(48, 104, 197, 0.16);
}

.solution-link {
  cursor: pointer;
  border: 1px solid rgba(19, 35, 45, 0.08);
  transition: transform .22s ease, box-shadow .22s ease, border-color .22s ease, background-color .22s ease;
}

.solution-link .card-body {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.solution-link__eyebrow {
  display: inline-flex;
  align-self: flex-start;
  margin-bottom: 14px;
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(31, 126, 150, 0.08);
  color: #2e7287;
  font-size: .72rem;
  font-weight: 700;
  letter-spacing: .06em;
  text-transform: uppercase;
}

.product-area-icon {
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  color: #267f96;
  background: linear-gradient(135deg, rgba(31, 126, 150, .14), rgba(16, 185, 129, .08));
  border: 1px solid rgba(31, 126, 150, .12);
}

.solution-link__cta {
  margin-top: auto;
  padding-top: 18px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  color: #0f172a;
  font-size: .92rem;
  font-weight: 700;
}

.solution-link__arrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  background: rgba(19, 35, 45, 0.08);
  transition: transform .22s ease, background-color .22s ease;
}

.solution-link:hover,
.solution-link:focus-visible {
  border-color: rgba(31, 126, 150, 0.28);
  box-shadow: 0 16px 44px rgba(31, 126, 150, 0.16);
  transform: translateY(-4px);
}

.solution-link:hover .solution-link__arrow,
.solution-link:focus-visible .solution-link__arrow {
  transform: translateX(3px);
  background: rgba(31, 126, 150, 0.14);
}
.studio-masonry {
  column-count: 1;
  column-gap: 16px;
}
@media (min-width: 576px) { .studio-masonry { column-count: 2; } }
@media (min-width: 992px) { .studio-masonry { column-count: 3; } }
.studio-card {
  display: inline-block;
  width: 100%;
  margin: 0 0 16px;
  break-inside: avoid;
}
/* Product story showcase */
.showcase-section {
  position: relative;
  overflow: hidden;
  background:
    radial-gradient(circle at 8% 18%, rgba(57, 207, 239, .13), transparent 28rem),
    radial-gradient(circle at 92% 78%, rgba(48, 173, 117, .12), transparent 30rem),
    #f6f8f5;
  border-block: 1px solid rgba(19, 35, 45, .07);
}

.showcase-heading {
  max-width: 820px;
  margin-bottom: 30px;
  text-align: center;
}

.showcase-eyebrow {
  margin: 0 0 12px;
  color: #22748a;
  font-size: .76rem;
  font-weight: 800;
  letter-spacing: .15em;
  text-transform: uppercase;
}

.showcase-title {
  max-width: 760px;
  margin: 0 auto;
  color: #13232d;
  font-size: clamp(2rem, 4vw, 3.4rem);
  font-weight: 750;
  letter-spacing: -.045em;
  line-height: 1.03;
}

.showcase-subheading {
  max-width: 700px;
  margin: 18px auto 0;
  color: #5d6d75;
  font-size: clamp(1rem, 1.5vw, 1.13rem);
  line-height: 1.65;
}

.showcase-viewport {
  position: relative;
  width: 100%;
}

.showcase-strip {
  position: relative;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 24px 0 20px;
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  touch-action: pan-x;
  -webkit-overflow-scrolling: touch;
}

:deep(.showcase-strip::-webkit-scrollbar) {
  display: none;
}

.showcase-track {
  display: flex;
  align-items: stretch;
  gap: clamp(18px, 3vw, 38px);
  width: max-content;
  padding: 0 max(24px, calc((100vw - 880px) / 2));
}

.shot-item {
  display: grid;
  grid-template-columns: minmax(300px, 360px) minmax(280px, 1fr);
  flex: 0 0 auto;
  width: min(760px, calc(100vw - 80px));
  min-height: 560px;
  overflow: hidden;
  color: #f8fafc;
  background: #121a17;
  border: 1px solid rgba(255, 255, 255, .12);
  border-radius: 30px;
  box-shadow: 0 18px 54px rgba(19, 35, 45, .12);
  cursor: pointer;
  opacity: .56;
  scroll-snap-align: center;
  transform: scale(.96);
  transition: opacity .3s ease, transform .3s ease, box-shadow .3s ease;
}

.shot-item.active {
  opacity: 1;
  transform: scale(1);
  box-shadow: 0 28px 80px rgba(19, 35, 45, .2);
}

.showcase-story__visual {
  position: relative;
  display: grid;
  min-width: 0;
  min-height: 560px;
  place-items: center;
  overflow: hidden;
  padding: 44px 38px 0;
  background:
    radial-gradient(circle at 50% 38%, rgba(57, 207, 239, .2), transparent 36%),
    linear-gradient(145deg, #1b2823, #0c1210);
}

.showcase-story__visual::after {
  position: absolute;
  inset: 8% 12%;
  border: 1px solid rgba(255, 255, 255, .08);
  border-radius: 999px;
  content: '';
  filter: blur(.2px);
  pointer-events: none;
}

.showcase-product-pill {
  position: absolute;
  top: 22px;
  left: 22px;
  z-index: 2;
  padding: 8px 12px;
  color: #d9fbff;
  background: rgba(12, 18, 16, .7);
  border: 1px solid rgba(111, 220, 237, .28);
  border-radius: 999px;
  font-size: .72rem;
  font-weight: 750;
  letter-spacing: .08em;
  text-transform: uppercase;
  backdrop-filter: blur(10px);
}

.shot-item picture {
  position: relative;
  z-index: 1;
  display: grid;
  width: 100%;
  height: 100%;
  place-items: end center;
}

.shot-img {
  display: block;
  max-width: 100%;
  background: #101410;
  box-shadow: 0 24px 60px rgba(0, 0, 0, .4);
}

.shot-item--phone .shot-img {
  width: auto;
  height: 500px;
  border: 7px solid #050706;
  border-bottom: 0;
  border-radius: 38px 38px 0 0;
}

.shot-item--desktop .showcase-story__visual {
  padding: 56px 28px;
}

.shot-item--desktop picture {
  place-items: center;
}

.shot-item--desktop .shot-img {
  width: 100%;
  height: auto;
  border: 6px solid #1e2925;
  border-radius: 16px;
}

.shot-item--admin-collage {
  grid-template-columns: minmax(380px, 480px) minmax(280px, 1fr);
  width: min(860px, calc(100vw - 80px));
}

.shot-item--admin-collage .showcase-story__visual {
  padding: 54px 26px 28px;
}

.showcase-admin-collage {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 430px;
  height: 472px;
  isolation: isolate;
}

.showcase-admin-collage::before {
  position: absolute;
  inset: 14% 6%;
  z-index: -1;
  background: rgba(67, 208, 232, .16);
  border-radius: 999px;
  content: '';
  filter: blur(48px);
}

.showcase-admin-collage picture.showcase-admin-panel {
  position: absolute;
  display: block;
  width: auto;
  height: auto;
  overflow: hidden;
  background: #303136;
  border: 1px solid rgba(255, 255, 255, .14);
  border-radius: 15px;
  box-shadow: 0 22px 50px rgba(0, 0, 0, .4), 0 1px 0 rgba(255, 255, 255, .08) inset;
  transition: transform .35s ease, box-shadow .35s ease;
}

.showcase-admin-panel img {
  display: block;
  width: 100%;
  height: auto;
}

.shot-item.active .showcase-admin-panel--sale-details {
  transform: rotate(-1.2deg) translateY(-3px);
}

.shot-item.active .showcase-admin-panel--sale-summary {
  transform: rotate(1.1deg) translateY(-5px);
}

.showcase-admin-collage--sales .showcase-admin-panel--sale-details {
  top: 18px;
  left: 0;
  z-index: 1;
  width: 59%;
  transform: rotate(-2.1deg);
}

.showcase-admin-collage--sales .showcase-admin-panel--sale-summary {
  top: 102px;
  right: 0;
  z-index: 3;
  width: 59%;
  transform: rotate(1.8deg);
}

.showcase-admin-collage--sales .showcase-admin-panel--sale-documents {
  bottom: 18px;
  left: 5%;
  z-index: 2;
  width: 49%;
  transform: rotate(-.6deg);
}

.showcase-admin-collage--insights::before {
  background: rgba(250, 192, 67, .14);
}

.showcase-admin-collage--insights .showcase-admin-panel--insights-risk {
  top: 10px;
  left: 18%;
  z-index: 1;
  width: 64%;
}

.showcase-admin-collage--insights .showcase-admin-panel--insights-visits {
  top: 255px;
  left: 0;
  z-index: 3;
  width: 100%;
  transform: rotate(-.5deg);
}

.showcase-admin-collage--insights .showcase-admin-panel--insights-credits {
  top: 344px;
  left: 5%;
  z-index: 2;
  width: 95%;
  transform: rotate(.45deg);
}

.showcase-story__copy {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 32px 30px 38px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, .025), transparent),
    #13201b;
}

.showcase-story__number {
  color: rgba(255, 255, 255, .3);
  font-size: .8rem;
  font-weight: 750;
  letter-spacing: .16em;
}

.showcase-story__kicker {
  margin: 0 0 12px;
  color: #6fdced;
  font-size: .76rem;
  font-weight: 750;
  letter-spacing: .08em;
  text-transform: uppercase;
}

.showcase-story__copy h3 {
  margin: 0;
  color: #fff;
  font-size: clamp(1.65rem, 2.35vw, 2.35rem);
  font-weight: 700;
  letter-spacing: -.035em;
  line-height: 1.08;
}

.showcase-story__copy p:last-child {
  margin: 18px 0 0;
  color: #b6c3bd;
  font-size: .98rem;
  line-height: 1.65;
}

.showcase-controls {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  max-width: 860px;
  margin-top: 10px;
  margin-bottom: 24px;
}

.showcase-counter {
  display: flex;
  gap: 7px;
  color: #738078;
  font-size: .78rem;
  font-weight: 700;
  letter-spacing: .12em;
}

.showcase-counter span:first-child {
  color: #18322a;
}

.showcase-dots {
  display: flex;
  align-items: center;
  gap: 8px;
}

.showcase-dot {
  width: 26px;
  height: 4px;
  padding: 0;
  background: rgba(19, 35, 45, .16);
  border: 0;
  border-radius: 999px;
  transition: width .2s ease, background-color .2s ease;
}

.showcase-dot.active {
  width: 48px;
  background: #237d91;
}

.showcase-arrows {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.showcase-arrow {
  display: grid;
  width: 44px;
  height: 44px;
  padding: 0;
  color: #18322a;
  background: rgba(255, 255, 255, .72);
  border: 1px solid rgba(19, 35, 45, .12);
  border-radius: 999px;
  box-shadow: 0 8px 22px rgba(19, 35, 45, .08);
  place-items: center;
  transition: color .2s ease, background-color .2s ease, transform .2s ease;
}

.showcase-arrow:hover,
.showcase-arrow:focus-visible {
  color: #fff;
  background: #18322a;
  transform: translateY(-2px);
}

@media (max-width: 767px) {
  .showcase-heading {
    padding-inline: 24px;
  }

  .showcase-title {
    font-size: clamp(2rem, 10vw, 2.75rem);
  }

  .showcase-track {
    gap: 16px;
    padding-inline: 16px;
  }

  .shot-item {
    grid-template-columns: 1fr;
    width: calc(100vw - 32px);
    min-height: 0;
    border-radius: 24px;
  }

  .showcase-story__visual {
    min-height: 470px;
    padding: 54px 22px 0;
  }

  .shot-item--phone .shot-img {
    height: 420px;
    border-width: 5px;
    border-radius: 29px 29px 0 0;
  }

  .shot-item--desktop .showcase-story__visual {
    min-height: 300px;
    padding: 60px 16px 30px;
  }

  .shot-item--admin-collage .showcase-story__visual {
    min-height: 440px;
    padding: 60px 18px 28px;
  }

  .showcase-admin-collage {
    max-width: 350px;
    height: 385px;
  }

  .showcase-admin-collage--sales .showcase-admin-panel--sale-summary {
    top: 84px;
  }

  .showcase-admin-collage--sales .showcase-admin-panel--sale-documents {
    bottom: 12px;
  }

  .showcase-admin-collage--insights .showcase-admin-panel--insights-visits {
    top: 208px;
  }

  .showcase-admin-collage--insights .showcase-admin-panel--insights-credits {
    top: 282px;
  }

  .showcase-story__copy {
    min-height: 260px;
    padding: 24px;
  }

  .showcase-story__copy h3 {
    max-width: 460px;
  }

  .showcase-controls {
    grid-template-columns: 1fr auto;
    padding-inline: 20px;
  }

  .showcase-dots {
    display: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .showcase-strip {
    scroll-behavior: auto;
  }

  .shot-item,
  .showcase-arrow,
  .showcase-dot {
    transition: none;
  }
}
.logo-scroller--animate:hover .logo-track { animation-play-state: paused; }
.logo-item { flex: 0 0 auto; display: flex; align-items: center; justify-content: center; flex-direction: column; min-width: 140px; padding: 6px 8px; }
.logo-item img {
  height: 50px;
  width: auto;
  filter: grayscale(1) contrast(.9) brightness(.95);
  opacity: .9;
  transition: filter .25s ease, opacity .25s ease;
  border-radius: 10px;
}
.logo-item:hover img { filter: none; opacity: 1; }
.logo-link { display: flex; align-items: center; justify-content: center; flex-direction: column; text-decoration: none; color: inherit; }
/* Glassy logo container */
.logo-link.logo-glassy {
  position: relative;
  padding: 10px 12px;
  border-radius: 14px;
  background: rgba(255,255,255,0.18);
  border: 1px solid rgba(255,255,255,0.35);
  backdrop-filter: blur(10px) saturate(160%);
  -webkit-backdrop-filter: blur(10px) saturate(160%);
  box-shadow: 0 8px 32px rgba(2,6,23,.08);
}
.logo-link.logo-glassy:hover {
  background-color: rgba(255,255,255,0.24);
  border-color: rgba(181,215,255,0.45);
}
.logo-caption { margin-top: 6px; font-size: .8rem; color: #64748b; white-space: nowrap; }
/* Per-logo caption overrides */
.caption-futura-upper { font-family: 'Futura-Medium'; text-transform: uppercase; }

@keyframes logo-scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }
@media (max-width: 576px) {
  .logo-scroller {
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    touch-action: pan-x;
    scrollbar-width: none;
    mask-image: none;
    -webkit-mask-image: none;
    padding-inline: 8px;
  }
  .logo-scroller::-webkit-scrollbar { display: none; }
  .logo-track {
    gap: 22px;
    margin: 0;
    padding-right: 8px;
  }
  .logo-item {
    min-width: 120px;
    scroll-snap-align: start;
  }
  .logo-item img { height: 50px; }
}
@media (prefers-reduced-motion: reduce) {
  .logo-track, .logo-scroller--animate .logo-track { animation: none !important; }
}

#pricing .card.card-pro.reveal.mt-3.mb-4.reveal-in {
  width: calc(100% - 24px);
  max-width: 700px;
  min-width: 350px;
  margin: 0 auto;
  top: 40px;
  padding: 10px;
}

hr.gradient-divider {
  border: 0;
  height: 2px;
  width: 100%;
  margin: 40px auto;
  background: rgb(137 125 125 / 60%);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  border-radius: 50px;
  box-shadow: 0 2px 8px rgb(0 0 0 / 73%);
}

.auto-flex{
  max-width: 100%;
  flex: auto;
}
</style>
